import { Controller } from "@hotwired/stimulus"
import { brandName } from "../utils/brand"
import { buildWorld, PALETTES, HAND_FONT, renderSprite, paintLily, paintLabel } from "../community_square/world"
import { SquareVoice } from "../community_square/voice"
import { joinByDefault, rememberVoice } from "../community_square/voice_pref"
import { Ambient } from "../community_square/ambient"

// The Square — the general Community room as a walkable pencil map.
//
// Walk with the arrow keys / WASD, or click (tap) a spot. Everyone on the map
// is a round token; a line someone says appears as a bubble over them, full
// strength within earshot and fading to nothing further out (the same
// `hearing` radii the server uses to decide which resident answers). Lines
// from the last day linger as small marks where they were said.
//
// This controller has no socket. community-chat owns the page's one
// CommunityChannel subscription and relays frames here as
// `community-square:frame` window events (roster, join, leave, move, said);
// this controller asks it to send with `community-square:send`
// ({ action: "move" | "ping", payload }) and keeps it told where we stand
// with `community-square:position`, so a line typed in the chat composer is
// said at our spot.
//
// Residents: Jane stands on her porch. A resident may instead walk a loop
// whose position is a pure function of the clock (config.residents[].loop),
// corrected for this browser's clock drift against the server's `now`.
export default class extends Controller {
    static targets = ["canvas", "count", "soundButton", "listenButton", "voiceButton", "cameraButton", "muteButton", "stageHint", "peoplePanel", "peopleList", "placeName", "placeBlurb", "placeAction", "placeWant", "placeRide", "placeTrip", "card", "cardName", "cardLink", "cardMute", "cardSafety", "cardBlock", "reportForm", "reportReason", "reportNote", "minimap", "placesButton", "placesPanel", "placesList", "placesFilter", "tip"]
    static values = { config: Object }

    connect() {
        const config = this.configValue
        this.config = config
        this.world = buildWorld(config)
        this.ambient = new Ambient(this.world, { seed: config.seed, reduceMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches })
        this.camReady = false
        this.ctx = this.canvasTarget.getContext("2d")
        this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        this.clockOffset = (config.now || Date.now() / 1000) - Date.now() / 1000
        this.full = config.hearing.full
        this.zero = config.hearing.zero

        const me = config.me
        this.me = { ...me, x: config.spawn.x, y: config.spawn.y, tx: null, ty: null, self: true }
        this.people = new Map() // id -> { id, name, initial, avatar, x, y, gx, gy }
        this.residents = config.residents.map((r) => ({ id: `r:${r.key}`, key: r.key, name: r.name, initial: r.initial, tone: r.tone, loop: r.loop, x: r.x, y: r.y, resident: true }))
        this.bubbles = [] // { speaker, text, x, y, born, life }
        this.notes = (config.lingering || []).filter((l) => !l.board).map((l) => ({ ...l }))
        this.boardNotes = (config.board_notes || []).map((l) => ({ ...l }))
        this.stageSpot = config.landmarks.find((l) => l.broadcast) || null
        this.images = new Map()
        this.keys = new Set()
        this.cam = { x: 0, y: 0 }
        this.currentPlace = undefined
        this.lastSentAt = 0
        this.lastSent = null
        this.clock = 0
        // getting around: the trip we're on (waiting on a platform, or riding)
        this.transport = this.world.transport
        this.trip = null
        this.riders = new Map()
        this.drawRider = (ctx, p, x, y, { scale = 1, lift = 0, clip = null } = {}) => {
            ctx.save()
            if (clip) { ctx.beginPath(); ctx.rect(...clip); ctx.clip() }
            const cy = y - lift
            ctx.translate(x, cy); ctx.scale(scale, scale); ctx.translate(-x, -cy)
            this.#drawToken(ctx, this.palette, p, { x, y: cy })
            ctx.restore()
        }

        this.voice = new SquareVoice({ iceServers: config.ice_servers, send: (action, payload) => this.#send(action, payload), onChange: () => this.#renderVoice() })
        // Blocks go both ways: `blocked` is everyone we must not see or hear;
        // `blockedByMe` is who we can unblock.
        this.blocked = new Set(config.blocked || [])
        this.blockedByMe = new Set(config.blocked_by_me || [])
        this.voice.setExcluded(this.blocked)
        this.voiceTick = 0

        this.onFrame = (e) => this.#receive(e.detail)
        window.addEventListener("community-square:frame", this.onFrame)
        this.onHelp = () => this.#showHelp()
        window.addEventListener("jane-worlds:help", this.onHelp)
        this.onKeyDown = (e) => this.#keyDown(e)
        this.onKeyUp = (e) => { const k = e.key.toLowerCase(); if (k === "shift") this.running = false; this.keys.delete(k) }
        this.onBlur = () => { this.keys.clear(); this.running = false }
        this.onPointer = (e) => this.#pointer(e)
        this.canvasTarget.addEventListener("keydown", this.onKeyDown)
        this.canvasTarget.addEventListener("keyup", this.onKeyUp)
        this.canvasTarget.addEventListener("blur", this.onBlur)
        this.canvasTarget.addEventListener("pointerdown", this.onPointer)
        this.onPointerMove = (e) => this.#pointerMove(e)
        this.onPointerUp = (e) => this.#pointerUp(e)
        this.onWheel = (e) => this.#wheel(e)
        this.canvasTarget.addEventListener("pointermove", this.onPointerMove)
        this.canvasTarget.addEventListener("pointerup", this.onPointerUp)
        this.canvasTarget.addEventListener("pointercancel", this.onPointerUp)
        this.canvasTarget.addEventListener("wheel", this.onWheel, { passive: false })
        this.touches = new Map()
        try { this.zoom = parseFloat(window.localStorage.getItem("community-square:zoom")) || 1 } catch (e) { this.zoom = 1 }

        this.resizeObserver = new ResizeObserver(() => this.#resize())
        this.resizeObserver.observe(this.canvasTarget)
        this.themeObserver = new MutationObserver(() => this.#paintStatic())
        this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-bs-theme"] })
        this.pingTimer = setInterval(() => this.#send("ping", {}), 25000)

        this.#resize()
        this.#maybeShowTip()
        this.#publishPosition()
        this.#updateCount()
        this.#restoreSoundPreference()
        this.lastT = performance.now()
        this.raf = requestAnimationFrame((t) => this.#frame(t))
    }

    disconnect() {
        cancelAnimationFrame(this.raf)
        clearInterval(this.pingTimer)
        window.removeEventListener("community-square:frame", this.onFrame)
        window.removeEventListener("jane-worlds:help", this.onHelp)
        this.resizeObserver?.disconnect()
        this.themeObserver?.disconnect()
        this.#stopSound()
        this.voice?.disable()
    }

    // --- network frames (relayed by community-chat) --------------------------

    #receive(data) {
        if (!data || !data.type) return
        switch (data.type) {
        case "hello":
            this.voice.hello(data.peer)
            this.#voiceByDefault()
            break
        case "voice":
            if (data.id !== this.me.id) this.voice.announce(data)
            break
        case "signal":
            this.voice.signal(data)
            break
        case "roster":
            (data.people || []).forEach((p) => this.#upsertPerson(p))
            break
        case "join":
            if (data.person?.id === this.me.id) {
                // The server placed us (a returning member resumes their spot).
                if (!this.movedYet) { this.me.x = data.person.x; this.me.y = data.person.y; this.#unstick(); this.#publishPosition() }
                this.ambient.arrive(this.me.x, this.me.y)
            } else {
                this.#upsertPerson(data.person)
                this.voice.reannounce()
                if (data.person) this.ambient.arrive(data.person.x, data.person.y)
            }
            break
        case "leave":
            this.people.delete(data.id)
            this.voice.forget(data.id, data.peer)
            break
        case "move": {
            if (data.id === this.me.id) break
            const p = this.people.get(data.id)
            if (p) { p.gx = data.x; p.gy = data.y; p.ride = data.ride || null }
            break
        }
        case "said":
            this.#said(data.said)
            break
        }
        this.#updateCount()
    }

    #upsertPerson(person) {
        if (!person || person.id === this.me.id) return
        const existing = this.people.get(person.id)
        if (existing) Object.assign(existing, person, { gx: person.x, gy: person.y })
        else this.people.set(person.id, { ...person, gx: person.x, gy: person.y })
    }

    #said(said) {
        if (!said || this.blocked.has(said.speaker)) return
        const life = Math.min(9, 4 + said.text.length / 18)
        this.bubbles = this.bubbles.filter((b) => b.speaker !== said.speaker)
        this.bubbles.push({ speaker: said.speaker, text: said.text, x: said.x, y: said.y, born: this.clock, life, stage: !!said.stage, name: said.name })
        if (said.board) {
            this.boardNotes.unshift({ ...said })
            this.boardNotes = this.boardNotes.slice(0, 12)
            if (this.currentPlace && this.config.landmarks.find((l) => l.key === this.currentPlace)?.pin_days) this.#renderBoard()
        } else {
            this.notes.push({ ...said })
            if (this.notes.length > 120) this.notes.shift()
        }
    }

    #send(action, payload) {
        window.dispatchEvent(new CustomEvent("community-square:send", { detail: { action, payload } }))
    }

    #publishPosition() {
        window.dispatchEvent(new CustomEvent("community-square:position", { detail: { x: Math.round(this.me.x), y: Math.round(this.me.y) } }))
    }

    // Everyone on the map we can see: other members, minus blocks.
    #visiblePeople() {
        return [...this.people.values()].filter((p) => !this.blocked.has(p.id))
    }

    #updateCount() {
        if (!this.hasCountTarget) return
        const others = this.#visiblePeople().length
        this.countTarget.textContent = others === 0 ? "Just you" : others === 1 ? "You and 1 other" : `You and ${others} others`
        if (this.hasPeoplePanelTarget && !this.peoplePanelTarget.hidden) this.#renderPeople()
    }

    // --- who's here -------------------------------------------------------------

    togglePeople() {
        if (!this.hasPeoplePanelTarget) return
        const open = this.peoplePanelTarget.hidden
        if (open) this.#closePlaces()
        this.peoplePanelTarget.hidden = !open
        this.countTarget.setAttribute("aria-expanded", open ? "true" : "false")
        if (open) this.#renderPeople()
    }

    // Nearest first, with how far away they are in plain words and what they
    // are doing; a row walks you over to them.
    #renderPeople() {
        const everyone = [...this.residents, ...this.#visiblePeople()]
            .map((p) => ({ p, d: Math.hypot(p.x - this.me.x, p.y - this.me.y) }))
            .sort((a, b) => a.d - b.d)
        const describe = ({ p, d }) => {
            const where = d < this.full ? "nearby" : d < this.zero ? "within earshot" : "far away"
            const place = this.config.landmarks.find((l) => Math.hypot(l.x - p.x, l.y - p.y) < l.r)
            const bits = [place ? `at ${place.label}` : where]
            if (p.resident) bits.push("resident")
            if (this.#onStage(p)) bits.push("on stage")
            if (this.voice.isSpeaker(p.id)) bits.push("talking")
            else if (this.voice.isListening(p.id)) bits.push("listening")
            return bits.join(" · ")
        }
        this.peopleListTarget.replaceChildren(...everyone.map((entry) => {
            const li = document.createElement("li")
            const button = document.createElement("button")
            button.type = "button"
            button.className = "cc-square-people-row"
            const name = document.createElement("span")
            name.className = "cc-square-people-name"
            name.textContent = entry.p.name
            const meta = document.createElement("span")
            meta.className = "cc-square-people-meta"
            meta.textContent = describe(entry)
            button.append(name, meta)
            button.addEventListener("click", () => this.#walkTo(entry.p))
            li.append(button)
            return li
        }))
        if (!everyone.length) {
            const li = document.createElement("li")
            li.className = "cc-square-people-empty"
            li.textContent = "Nobody else is here right now."
            this.peopleListTarget.replaceChildren(li)
        }
    }

    // --- places --------------------------------------------------------------
    // Every landmark, grouped by area and filterable by name or app; a row
    // walks you there (the long way runs). The list is the map's index.

    togglePlaces() {
        if (!this.hasPlacesPanelTarget) return
        const open = this.placesPanelTarget.hidden
        if (!open) { this.#closePlaces(); this.canvasTarget.focus({ preventScroll: true }); return }
        if (this.hasPeoplePanelTarget) { this.peoplePanelTarget.hidden = true; this.countTarget.setAttribute("aria-expanded", "false") }
        this.placesPanelTarget.hidden = false
        this.placesButtonTarget.setAttribute("aria-expanded", "true")
        this.placesFilterTarget.value = ""
        this.#renderPlaces()
        this.placesFilterTarget.focus()
    }

    filterPlaces() { this.#renderPlaces() }

    // Enter in the filter walks to the first match; Escape closes.
    placesKey(e) {
        if (e.key === "Enter") {
            e.preventDefault()
            const first = this.placesListTarget.querySelector("button")
            if (first) first.click()
        } else if (e.key === "Escape") {
            this.#closePlaces()
            this.canvasTarget.focus({ preventScroll: true })
        }
    }

    #closePlaces() {
        if (!this.hasPlacesPanelTarget) return
        this.placesPanelTarget.hidden = true
        this.placesButtonTarget.setAttribute("aria-expanded", "false")
    }

    #renderPlaces() {
        const q = this.placesFilterTarget.value.trim().toLowerCase()
        const matches = this.config.landmarks.filter((l) => !q || [l.label, l.area, l.action, l.blurb].some((t) => (t || "").toLowerCase().includes(q)))
        const areas = new Map()
        matches.forEach((l) => { if (!areas.has(l.area)) areas.set(l.area, []); areas.get(l.area).push(l) })
        const rows = []
        areas.forEach((places, area) => {
            const head = document.createElement("li")
            head.className = "cc-square-places-area"
            head.textContent = area || "Elsewhere"
            rows.push(head)
            places.forEach((l) => {
                const li = document.createElement("li")
                const button = document.createElement("button")
                button.type = "button"
                button.className = "cc-square-people-row"
                const name = document.createElement("span")
                name.className = "cc-square-people-name"
                name.textContent = l.label
                const meta = document.createElement("span")
                meta.className = "cc-square-people-meta"
                const d = Math.hypot(l.x - this.me.x, l.y - this.me.y)
                meta.textContent = [l.action ? l.action.replace(/^Open /, "Opens ").replace(/^Play /, "Plays ") : null, d < l.r ? "you're here" : d < 700 ? "a short walk" : d < 2000 ? "a walk" : "a long walk"].filter(Boolean).join(" · ")
                button.append(name, meta)
                button.addEventListener("click", () => {
                    this.#closePlaces()
                    this.#goTo(l.x, l.y + l.r * 0.35)
                    this.canvasTarget.focus({ preventScroll: true })
                })
                li.append(button)
                rows.push(li)
            })
        })
        if (!rows.length) {
            const li = document.createElement("li")
            li.className = "cc-square-people-empty"
            li.textContent = "No place by that name."
            rows.push(li)
        }
        this.placesListTarget.replaceChildren(...rows)
    }

    // --- the first visit ----------------------------------------------------------

    #maybeShowTip() {
        if (!this.hasTipTarget) return
        let seen = false
        try { seen = window.localStorage.getItem("community-square:tip-seen") === "1" } catch (e) { seen = true }
        this.tipTarget.hidden = seen
    }

    // The world bar's keyboard button (and ?): the Square's controls are its tip.
    #showHelp() {
        if (this.hasTipTarget) this.tipTarget.hidden = !this.tipTarget.hidden
    }

    dismissTip() {
        this.tipTarget.hidden = true
        try { window.localStorage.setItem("community-square:tip-seen", "1") } catch (e) { /* noop */ }
        this.canvasTarget.focus({ preventScroll: true })
    }

    #walkTo(p) {
        const a = Math.atan2(this.me.y - p.y, this.me.x - p.x)
        this.#goTo(p.x + Math.cos(a) * 40, p.y + Math.sin(a) * 40)
        this.canvasTarget.focus({ preventScroll: true })
    }

    // --- input -----------------------------------------------------------------

    #keyDown(e) {
        const k = e.key.toLowerCase()
        if (k === "shift") { this.running = true; return }
        if (["arrowup", "arrowdown", "arrowleft", "arrowright", "w", "a", "s", "d"].includes(k)) {
            // riding, you stay in your seat; waiting, walking off means not now
            if (this.trip?.state === "riding") { e.preventDefault(); return }
            if (this.trip) this.#cancelTrip()
            this.keys.add(k)
            this.me.tx = null
            this.me.path = null
            this.#hideCard()
            this.#resumeSoundIfWanted()
            e.preventDefault()
        } else if (k === "enter") {
            // Enter to talk: over to the chat composer
            window.dispatchEvent(new CustomEvent("community-chat:open"))
            const input = document.querySelector("[data-community-chat-target='input']")
            if (input) { e.preventDefault(); input.focus() }
        } else if (k === "escape") {
            this.#hideCard()
            this.#closePlaces()
            if (this.hasPeoplePanelTarget) {
                this.peoplePanelTarget.hidden = true
                this.countTarget.setAttribute("aria-expanded", "false")
            }
        } else if (k === "+" || k === "=") {
            this.zoomIn(); e.preventDefault()
        } else if (k === "-" || k === "_") {
            this.zoomOut(); e.preventDefault()
        } else if (k === "0") {
            this.#setZoom(1); e.preventDefault()
        }
    }

    #pointer(e) {
        this.canvasTarget.focus({ preventScroll: true })
        this.#resumeSoundIfWanted()
        // Touch: wait for the finger to lift, so a pinch never walks you off.
        if (e.pointerType === "touch") {
            this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY, startX: e.clientX, startY: e.clientY })
            if (this.touches.size === 2) { this.pinch = { dist: this.#touchDistance(), zoom: this.zoom }; this.pinched = true }
            return
        }
        this.#tap(e)
    }

    #pointerMove(e) {
        const t = this.touches.get(e.pointerId)
        if (!t) return
        t.x = e.clientX; t.y = e.clientY
        if (this.pinch && this.touches.size === 2) {
            const d = this.#touchDistance()
            if (this.pinch.dist > 0) this.#setZoom(this.pinch.zoom * (d / this.pinch.dist))
        }
    }

    #pointerUp(e) {
        const t = this.touches.get(e.pointerId)
        if (!t) return
        this.touches.delete(e.pointerId)
        if (this.touches.size < 2) this.pinch = null
        if (this.touches.size === 0) {
            const moved = Math.hypot(t.x - t.startX, t.y - t.startY) > 12
            if (!this.pinched && !moved) this.#tap(e)
            this.pinched = false
        }
    }

    #touchDistance() {
        const [a, b] = [...this.touches.values()]
        return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0
    }

    // The wheel (and a trackpad pinch, which arrives as ctrl+wheel) zooms.
    #wheel(e) {
        e.preventDefault()
        const speed = e.ctrlKey ? 0.012 : 0.0018
        this.#setZoom(this.zoom * Math.exp(-e.deltaY * speed))
    }

    // --- zoom ---------------------------------------------------------------------
    // `zoom` multiplies the natural scale (the map covering the view). Zoomed
    // all the way out the whole map fits; all the way in is twice the natural
    // size. The camera stays on you. Sprites are redrawn at the new sharpness
    // once the zoom settles; until then the old ones stand in.

    zoomIn() { this.#setZoom(this.zoom * 1.25); this.#backToMap() }
    zoomOut() { this.#setZoom(this.zoom / 1.25); this.#backToMap() }

    #setZoom(zoom) {
        const { min, max } = this.#zoomRange()
        this.zoom = Math.max(min, Math.min(max, zoom))
        this.#applyScale()
        try { window.localStorage.setItem("community-square:zoom", String(this.zoom)) } catch (e) { /* noop */ }
        clearTimeout(this.zoomSettle)
        this.zoomSettle = setTimeout(() => this.#resharpen(), 350)
    }

    #zoomRange() {
        const cssW = this.canvasTarget.clientWidth || 1, cssH = this.canvasTarget.clientHeight || 1
        const base = this.#baseScale()
        const fit = Math.min(cssW / this.world.W, cssH / this.world.H)
        return { min: fit / base, max: Math.max(2, 1.1 / base) }
    }

    #baseScale() {
        const cssW = this.canvasTarget.clientWidth, cssH = this.canvasTarget.clientHeight
        return Math.max(cssW / this.world.W, cssH / this.world.H, cssW < 640 ? 0.62 : 0.5)
    }

    #applyScale() {
        const { min, max } = this.#zoomRange()
        this.zoom = Math.max(min, Math.min(max, this.zoom || 1))
        this.scale = this.#baseScale() * this.zoom
    }

    #resharpen() {
        const k = Math.min(2, Math.max(1, this.scale * (this.dpr || 1)))
        if (Math.abs(k - (this.spriteK || 1)) / (this.spriteK || 1) < 0.25) return
        this.staleSprites = this.spriteCanvases
        this.spriteK = k
        this.spriteCanvases = new Array(this.world.sprites.length)
        this.labelPalette = null
        this.#warmUp()
    }

    #tap(e) {
        const rect = this.canvasTarget.getBoundingClientRect()
        const wx = (e.clientX - rect.left) / this.scale + this.cam.x
        const wy = (e.clientY - rect.top) / this.scale + this.cam.y
        const hit = [...this.residents, ...this.#visiblePeople()].find((p) => Math.hypot(p.x - wx, p.y - wy) < 22)
        if (hit) { this.#showCard(hit, e.clientX - rect.left, e.clientY - rect.top); return }
        this.#hideCard()
        if (this.trip?.state === "riding") return
        this.#goTo(wx, wy)
    }

    #showCard(person, px, py) {
        if (!this.hasCardTarget) return
        this.cardNameTarget.textContent = person.name
        let href = null, label = null
        if (person.key === "jane") { href = this.config.jane_chat_path; label = "Talk privately" }
        else if (person.profile_path) { href = person.profile_path; label = "View profile" }
        if (href) { this.cardLinkTarget.href = href; this.cardLinkTarget.textContent = label; this.cardLinkTarget.hidden = false }
        else { this.cardLinkTarget.hidden = true }
        this.cardPerson = person
        this.cardAt = [px, py]
        if (this.hasCardMuteTarget) {
            const canMute = !person.resident && this.voice.isSpeaker(person.id)
            this.cardMuteTarget.hidden = !canMute
            this.cardMuteTarget.textContent = this.voice.blocked.has(person.id) ? "Unmute for me" : "Mute for me"
        }
        if (this.hasCardSafetyTarget) {
            this.cardSafetyTarget.hidden = !!person.resident
            this.cardBlockTarget.textContent = this.blockedByMe.has(person.id) ? "Unblock" : "Block"
        }
        if (this.hasReportFormTarget) this.reportFormTarget.hidden = true
        this.cardTarget.hidden = false
        const box = this.canvasTarget.getBoundingClientRect()
        this.cardTarget.style.left = `${Math.min(Math.max(px - 80, 8), box.width - 176)}px`
        this.cardTarget.style.top = `${Math.max(py - 86, 8)}px`
    }

    #hideCard() {
        if (this.hasCardTarget) this.cardTarget.hidden = true
    }

    // --- sound -----------------------------------------------------------------
    // Two looping <audio> elements from Zen's library (public/sounds): the
    // campfire, and Jane Radio, whose track is picked from the (server-
    // corrected) clock so everyone near the radio hears the same one. Volume
    // follows distance. Off until the member turns it on; the choice is
    // remembered, and resumes on their first keypress or click after a reload.

    toggleSound() {
        if (this.soundOn) { this.#stopSound(); this.#rememberSound(false) }
        else { this.#startSound(); this.#rememberSound(true) }
        this.#backToMap()
    }

    // After a HUD button, keys go back to walking.
    #backToMap() {
        this.canvasTarget.focus({ preventScroll: true })
    }

    #startSound() {
        const zones = this.config.landmarks.filter((l) => l.sound)
        // the river is heard along its whole length, not from one spot
        if (this.world.river && this.config.river?.sound) zones.push({ key: "river", sound: this.config.river.sound, river: true })
        // and the surf along the whole coast
        if (this.world.coast && this.config.coast_sound) zones.push({ key: "sea", sound: this.config.coast_sound, sea: true })
        // and the wind over the Reach's snowfields, softly
        if (this.world.reach?.active && this.config.snow_sound) zones.push({ key: "wind", sound: this.config.snow_sound, wind: true, gain: 0.45 })
        // the south: birdsong in the Wildwood, a dry wind over the Dunes
        if (this.world.wildwood?.active) zones.push({ key: "wildwood", sound: "forest", biomes: ["rainforest"], gain: 0.7 })
        if (this.world.dunes?.active) zones.push({ key: "dunes", sound: "brownnoise", biomes: ["desert", "canyon"], gain: 0.3 })
        this.sounds = zones.map((zone) => {
            const audio = new Audio()
            audio.loop = true
            audio.volume = 0
            audio.preload = "auto"
            return { zone, audio, track: null }
        })
        this.soundOn = true
        this.#syncSoundTracks()
        this.#renderSoundButton()
    }

    #stopSound() {
        (this.sounds || []).forEach((s) => { try { s.audio.pause() } catch (e) { /* noop */ } })
        this.sounds = []
        this.soundOn = false
        this.#renderSoundButton()
    }

    #syncSoundTracks() {
        (this.sounds || []).forEach((s) => {
            const track = s.zone.sound === "radio" ? this.#radioTrack() : s.zone.sound
            if (track === s.track) return
            s.track = track
            s.audio.src = `/sounds/${track}.mp3`
            s.audio.play().catch(() => {})
        })
    }

    #radioTrack() {
        const radio = this.config.radio
        const slot = Math.floor(this.#now() / radio.slot)
        return radio.tracks[slot % radio.tracks.length]
    }

    #distanceToRiver() {
        let best = Infinity
        const samples = this.world.river.samples
        for (let i = 0; i < samples.length; i += 3) {
            const d = Math.hypot(samples[i].x - this.me.x, samples[i].y - this.me.y)
            if (d < best) best = d
        }
        return Math.max(0, best - this.world.river.width / 2)
    }

    // The wind: loudest on the snow and the heights, fading toward the meadows.
    #windDistance() {
        const biome = this.world.biome(this.me.x, this.me.y)
        if (biome === "crag" || biome === "peak-snow") return 0
        if (biome === "alpine") return 260
        return this.world.reach.snowDistance(this.me.x, this.me.y)
    }

    // The surf along the whole coast, the Reach's shingle and fjord too; and
    // out at sea (on the ferry) you are in the middle of it.
    #distanceToCoast() {
        if (this.world.onFerry(this.me.x, this.me.y) || this.world.biome(this.me.x, this.me.y) === "tropical") return 0
        let best = Infinity
        ;[this.world.coast, this.world.northCoast || []].forEach((coast) => {
            for (let i = 0; i < coast.length; i += 3) {
                const d = Math.hypot(coast[i].x - this.me.x, coast[i].y - this.me.y)
                if (d < best) best = d
            }
        })
        return Math.max(0, best - 40)
    }

    #renderSoundButton() {
        if (!this.hasSoundButtonTarget) return
        this.soundButtonTarget.setAttribute("aria-pressed", this.soundOn ? "true" : "false")
        this.soundButtonTarget.innerHTML = this.soundOn
            ? '<i class="bi bi-volume-up" aria-hidden="true"></i><span>Sound on</span>'
            : '<i class="bi bi-volume-mute" aria-hidden="true"></i><span>Sound off</span>'
    }

    #rememberSound(on) {
        try { window.localStorage.setItem("community-square:sound", on ? "1" : "0") } catch (e) { /* noop */ }
    }

    #restoreSoundPreference() {
        try { this.wantSound = window.localStorage.getItem("community-square:sound") === "1" } catch (e) { this.wantSound = false }
    }

    #resumeSoundIfWanted() {
        if (this.wantSound && !this.soundOn) { this.wantSound = false; this.#startSound() }
    }

    // --- voice -----------------------------------------------------------------
    // Off until the member turns it on. Listen hears the people near you (and
    // the stage) without a microphone; Voice also turns the mic on (the
    // browser asks then). Voices are louder the closer they stand; see
    // community_square/voice.js.

    // Voice is on by default (voice_pref.js): the first hello joins it, once.
    async #voiceByDefault() {
        if (this.voiceTried) return
        this.voiceTried = true
        const joined = await joinByDefault(this.voice)
        if (joined === "talking" && this.hasPlaceBlurbTarget) this.placeBlurbTarget.textContent = "Voice is on: people near you can hear you. Mute or turn it off up top."
    }

    async toggleListen() {
        this.#backToMap()
        if (this.voice.enabled) { this.voice.disable(); rememberVoice(false); return }
        try {
            await this.voice.listen()
        } catch (e) {
            if (this.hasListenButtonTarget) this.listenButtonTarget.querySelector("span").textContent = "Audio unavailable"
        }
    }

    async toggleVoice() {
        this.#backToMap()
        if (this.voice.talking) { this.voice.stopTalking(); rememberVoice(false); return }
        try {
            await this.voice.enable()
            rememberVoice(true)
        } catch (e) {
            if (this.hasVoiceButtonTarget) this.voiceButtonTarget.querySelector("span").textContent = "Mic unavailable"
        }
    }

    async toggleCamera() {
        this.#backToMap()
        if (this.voice.camera) { this.voice.disableCamera(); return }
        try {
            await this.voice.enableCamera()
        } catch (e) {
            if (this.hasCameraButtonTarget) this.cameraButtonTarget.querySelector("span").textContent = "Camera unavailable"
        }
    }

    toggleMute() {
        this.voice.setMuted(!this.voice.muted)
        this.#backToMap()
    }

    // --- block and report ------------------------------------------------------
    // Both go through CommunitySafetyController. Blocking takes effect here at
    // once: their token, bubbles, lines and voice disappear for us.

    async blockCardPerson() {
        const person = this.cardPerson
        if (!person || person.resident) return
        const userId = person.id.replace(/^u/, "")
        const unblocking = this.blockedByMe.has(person.id)
        const ok = await this.#safetyRequest(unblocking ? `${this.config.safety.block_path}/${userId}` : this.config.safety.block_path, unblocking ? "DELETE" : "POST", { user_id: userId })
        if (!ok) return
        if (unblocking) { this.blockedByMe.delete(person.id); this.blocked.delete(person.id) }
        else this.#applyBlock(person.id)
        this.voice.setExcluded(this.blocked)
        this.#hideCard()
        this.#updateCount()
    }

    openReport() {
        if (!this.hasReportFormTarget || !this.cardPerson) return
        if (!this.reportReasonTarget.options.length) {
            this.config.safety.reasons.forEach((r) => this.reportReasonTarget.add(new Option(r.label, r.key)))
        }
        this.reportNoteTarget.value = ""
        this.reportFormTarget.hidden = false
        this.reportReasonTarget.focus()
    }

    async submitReport(event) {
        event.preventDefault()
        const person = this.cardPerson
        if (!person || person.resident) return
        const ok = await this.#safetyRequest(this.config.safety.report_path, "POST", {
            user_id: person.id.replace(/^u/, ""),
            reason: this.reportReasonTarget.value,
            note: this.reportNoteTarget.value,
            organization_id: this.config.safety.organization_id
        })
        if (!ok) return
        this.#applyBlock(person.id)
        this.voice.setExcluded(this.blocked)
        this.#hideCard()
        this.#updateCount()
        if (this.hasPlaceBlurbTarget) this.placeBlurbTarget.textContent = `Thanks. ${person.name} is blocked for you, and ${brandName()}'s team will look at the report.`
    }

    #applyBlock(id) {
        this.blocked.add(id)
        this.blockedByMe.add(id)
        this.bubbles = this.bubbles.filter((b) => b.speaker !== id)
        this.notes = this.notes.filter((n) => n.speaker !== id)
        window.dispatchEvent(new CustomEvent("community-square:blocked", { detail: { ids: [...this.blocked] } }))
    }

    async #safetyRequest(url, method, body) {
        try {
            const token = document.querySelector("meta[name='csrf-token']")?.content
            const response = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token || "" },
                body: method === "DELETE" ? null : JSON.stringify(body),
                credentials: "same-origin"
            })
            return response.ok
        } catch (e) {
            return false
        }
    }

    muteCardPerson() {
        if (!this.cardPerson) return
        this.voice.toggleBlocked(this.cardPerson.id)
        this.#showCard(this.cardPerson, this.cardAt[0], this.cardAt[1])
    }

    // "Someone is on stage · Listen" whenever a stage talk is on and we
    // aren't hearing it yet.
    #renderStageHint() {
        if (!this.hasStageHintTarget) return
        const speaker = this.onStageId ? this.people.get(this.onStageId) : null
        const show = !!speaker && this.voice.isSpeaker(speaker.id) && !this.voice.enabled && !this.blocked.has(speaker.id)
        this.stageHintTarget.hidden = !show
        if (show) this.stageHintTarget.textContent = `${speaker.name} is on stage · Listen`
    }

    #updateVoice() {
        this.#renderStageHint()
        if (!this.voice.enabled) return
        const positions = new Map()
        this.people.forEach((p) => positions.set(p.id, p))
        this.voice.update(this.me, positions, this.config.hearing, { stage: this.onStageId, meOnStage: this.#onStage(this.me) })
        // Observable voice state, for tests and debugging: how many peers are
        // connected, and whether any of their audio is actually arriving.
        const peers = [...this.voice.peers.values()]
        this.element.dataset.voiceConnected = String(peers.filter((p) => p.pc.connectionState === "connected").length)
        this.element.dataset.voiceHearing = String(peers.some((p) => p.level > 0.02))
        this.element.dataset.videoReceiving = String(peers.some((p) => p.video && p.video.videoWidth > 0))
    }

    #renderVoice() {
        if (!this.hasVoiceButtonTarget) return
        const on = this.voice.talking
        if (this.hasListenButtonTarget) {
            const listening = this.voice.enabled
            this.listenButtonTarget.setAttribute("aria-pressed", listening ? "true" : "false")
            this.listenButtonTarget.innerHTML = listening
                ? '<i class="bi bi-headphones" aria-hidden="true"></i><span>Listening</span>'
                : '<i class="bi bi-headphones" aria-hidden="true"></i><span>Listen</span>'
        }
        this.#renderStageHint()
        if (this.hasCameraButtonTarget) {
            const cam = this.voice.camera
            this.cameraButtonTarget.setAttribute("aria-pressed", cam ? "true" : "false")
            this.cameraButtonTarget.innerHTML = cam
                ? '<i class="bi bi-camera-video-fill" aria-hidden="true"></i><span>Camera on</span>'
                : '<i class="bi bi-camera-video-off" aria-hidden="true"></i><span>Camera</span>'
        }
        this.voiceButtonTarget.setAttribute("aria-pressed", on ? "true" : "false")
        this.voiceButtonTarget.innerHTML = on
            ? '<i class="bi bi-mic-fill" aria-hidden="true"></i><span>Voice on</span>'
            : '<i class="bi bi-mic-mute" aria-hidden="true"></i><span>Voice off</span>'
        if (this.hasMuteButtonTarget) {
            this.muteButtonTarget.hidden = !on
            this.muteButtonTarget.setAttribute("aria-pressed", this.voice.muted ? "true" : "false")
            this.muteButtonTarget.innerHTML = this.voice.muted
                ? '<i class="bi bi-mic-mute-fill" aria-hidden="true"></i><span>Muted</span>'
                : '<i class="bi bi-mic" aria-hidden="true"></i><span>Mute</span>'
        }
    }

    // --- layout ----------------------------------------------------------------

    #resize() {
        const cssW = this.canvasTarget.clientWidth, cssH = this.canvasTarget.clientHeight
        if (!cssW || !cssH) return
        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        this.canvasTarget.width = Math.round(cssW * dpr)
        this.canvasTarget.height = Math.round(cssH * dpr)
        this.dpr = dpr
        // Naturally the map covers the canvas without zooming out so far that
        // tokens turn to specks; the member's zoom scales that (see #setZoom),
        // and the camera follows them across whatever doesn't fit.
        this.#applyScale()
        this.#paintStatic()
    }

    // The ground (meadow, paths, pond, clearings) is painted into canvases once
    // per palette; every upright thing (trees, buildings, benches, lamps) gets
    // its own small canvas, so each frame is a handful of drawImage calls
    // sorted by where things touch the ground. Repainted on resize and theme
    // change. Sprites are only drawn the first time they come on screen.
    //
    // The ground is three layers, coarse to fine, so it is as sharp as the
    // screen needs however big the map grows:
    //   - the overview: the whole map at low resolution (up to 6 megapixels),
    //     for seeing most of the map at once and for the minimap, and the
    //     stand-in wherever a finer layer isn't painted yet;
    //   - chunks: 2000×2500-unit pieces at the resolution the whole map got
    //     when it was 4000×5000 and fit one 12-megapixel canvas. Painted as
    //     they come into view, a strip at a time in idle moments, and let go
    //     when far away, so doubling the map never halved the ground's
    //     resolution and memory stays bounded;
    //   - tiles: 512-unit pieces at full screen resolution, zoomed in on retina.
    // world.paintGround paints any region exactly as it paints the whole map,
    // so the layers always agree.

    static CHUNK_W = 2000
    static CHUNK_H = 2500
    static STRIP = 500
    static REFERENCE_AREA = 4000 * 5000
    static MAX_CHUNKS = (navigator.deviceMemory && navigator.deviceMemory < 4) ? 4 : 8

    #paintStatic() {
        this.palette = document.documentElement.getAttribute("data-bs-theme") === "dark" ? PALETTES.night : PALETTES.day
        const W = this.world.W, H = this.world.H
        // painted for the closest zoom, so zooming in never needs a repaint
        const cap = Math.sqrt(12e6 / this.constructor.REFERENCE_AREA)
        const closest = this.#baseScale() * this.#zoomRange().max
        const k = Math.min(2, cap, Math.max(0.75, closest * (this.dpr || 1)))
        this.groundK = k
        this.groundTiles = new Map()
        this.tileQueue = new Set()
        const ok = Math.min(k, Math.sqrt(6e6 / (W * H)))
        const overview = document.createElement("canvas")
        overview.width = Math.round(W * ok)
        overview.height = Math.round(H * ok)
        this.overview = { canvas: overview, k: ok, x: 0, y: 0, w: W, h: H, m: 0, strips: new Array(Math.ceil(H / this.constructor.STRIP)).fill(false), done: false }
        this.chunks = new Map()
        this.minimapBase = null
        // the chunks you can see now are painted on the spot, so the first
        // frame is sharp; everything else fills in while idle
        const cssW = this.canvasTarget.clientWidth || 1, cssH = this.canvasTarget.clientHeight || 1
        const viewW = cssW / (this.scale || 1), viewH = cssH / (this.scale || 1)
        const at = this.me || this.config.spawn
        const cam = { x: Math.max(0, Math.min(W - viewW, at.x - viewW / 2)), y: Math.max(0, Math.min(H - viewH, at.y - viewH / 2)) }
        this.#chunksIn(cam.x, cam.y, viewW, viewH).forEach((chunk) => chunk.strips.forEach((_, i) => this.#paintStrip(chunk, i)))
        this.spriteK = Math.min(1.6, Math.max(1, (this.scale || 1) * (this.dpr || 1)))
        this.spriteCanvases = new Array(this.world.sprites.length)
        this.staleSprites = null
        this.#scheduleGround()
        this.#warmUp()
    }

    // The chunks overlapping a view, made (unpainted) as needed unless
    // `create` is false.
    #chunksIn(x, y, w, h, create = true) {
        const CW = this.constructor.CHUNK_W, CH = this.constructor.CHUNK_H
        const out = []
        for (let cy = Math.max(0, Math.floor(y / CH) * CH); cy < Math.min(this.world.H, y + h); cy += CH) {
            for (let cx = Math.max(0, Math.floor(x / CW) * CW); cx < Math.min(this.world.W, x + w); cx += CW) {
                const chunk = create ? this.#chunk(cx, cy) : this.chunks.get(`${cx},${cy}`)
                if (chunk) out.push(chunk)
            }
        }
        return out
    }

    // A chunk's canvas reaches `m` units past its edges, so neighbouring
    // chunks overlap and are drawn a unit larger: no hairline between them.
    #chunk(cx, cy) {
        const key = `${cx},${cy}`
        let chunk = this.chunks.get(key)
        if (chunk) return chunk
        const m = 2
        const w = Math.min(this.constructor.CHUNK_W, this.world.W - cx), h = Math.min(this.constructor.CHUNK_H, this.world.H - cy)
        const canvas = document.createElement("canvas")
        canvas.width = Math.ceil((w + m * 2) * this.groundK)
        canvas.height = Math.ceil((h + m * 2) * this.groundK)
        chunk = { key, canvas, k: this.groundK, x: cx, y: cy, w, h, m, strips: new Array(Math.ceil(h / this.constructor.STRIP)).fill(false), done: false, last: performance.now() }
        this.chunks.set(key, chunk)
        return chunk
    }

    // Paint one horizontal strip of a layer (the overview or a chunk). The
    // clip is whole device pixels, so strips meet without a seam; the first
    // and last strips take the layer's margin with them.
    #paintStrip(layer, i) {
        if (layer.strips[i]) return
        const S = this.constructor.STRIP, k = layer.k, m = layer.m
        const top = i === 0 ? -m : i * S, bottom = i === layer.strips.length - 1 ? layer.h + m : (i + 1) * S
        const py0 = Math.round((top + m) * k), py1 = Math.round((bottom + m) * k)
        const g = layer.canvas.getContext("2d")
        g.save()
        g.setTransform(1, 0, 0, 1, 0, 0)
        g.beginPath(); g.rect(0, py0, layer.canvas.width, py1 - py0); g.clip()
        g.setTransform(k, 0, 0, k, -(layer.x - m) * k, -(layer.y - m) * k)
        this.world.paintGround(g, this.palette, { x: layer.x - m - 8, y: layer.y + top - 8, w: layer.w + m * 2 + 16, h: bottom - top + 16 })
        g.restore()
        layer.strips[i] = true
        layer.done = layer.strips.every(Boolean)
        if (layer === this.overview && layer.done) this.#paintMinimapBase()
    }

    // Idle work, one strip at a time: the chunks in view first (nearest
    // strip first), then the overview, then the chunks around the view.
    #scheduleGround() {
        if (this.groundPending) return
        this.groundPending = true
        const idle = window.requestIdleCallback || ((fn) => setTimeout(() => fn({ timeRemaining: () => 8 }), 24))
        // no token: a repaint replaces the layers, and the next job is always
        // picked from whatever layers exist when the callback runs
        idle((deadline) => {
            this.groundPending = false
            if (!this.element.isConnected) return
            let worked = false
            do {
                const job = this.#nextGroundJob()
                if (!job) break
                this.#paintStrip(job.layer, job.i)
                worked = true
            } while (deadline.timeRemaining() > 12)
            this.#evictChunks()
            if (worked) this.#scheduleGround()
        })
    }

    #nextGroundJob() {
        const view = this.#viewRect()
        const cx = view.x + view.w / 2, cy = view.y + view.h / 2
        const S = this.constructor.STRIP
        const pick = (layers) => {
            let best = null
            layers.forEach((layer) => layer.strips.forEach((painted, i) => {
                if (painted) return
                const d = Math.hypot(layer.x + layer.w / 2 - cx, layer.y + i * S + S / 2 - cy)
                if (!best || d < best.d) best = { layer, i, d }
            }))
            return best
        }
        const needChunks = this.scale * (this.dpr || 1) > this.overview.k * 1.15
        if (needChunks) {
            const job = pick(this.#chunksIn(view.x, view.y, view.w, view.h))
            if (job) return job
        }
        if (!this.overview.done) return pick([this.overview])
        if (!needChunks) return null
        // then the neighbours, while there is room for them
        const around = [view.x - view.w * 0.5, view.y - view.h * 0.5, view.w * 2, view.h * 2]
        const job = pick(this.#chunksIn(...around, false))
        if (job || this.chunks.size >= this.constructor.MAX_CHUNKS) return job
        const CW = this.constructor.CHUNK_W, CH = this.constructor.CHUNK_H
        const next = []
        for (let y = Math.max(0, Math.floor(around[1] / CH) * CH); y < Math.min(this.world.H, around[1] + around[3]); y += CH) {
            for (let x = Math.max(0, Math.floor(around[0] / CW) * CW); x < Math.min(this.world.W, around[0] + around[2]); x += CW) {
                if (!this.chunks.has(`${x},${y}`)) next.push({ x, y, d: Math.hypot(x + CW / 2 - cx, y + CH / 2 - cy) })
            }
        }
        next.sort((a, b) => a.d - b.d)
        return next.length ? pick([this.#chunk(next[0].x, next[0].y)]) : null
    }

    #viewRect() {
        const cssW = this.canvasTarget.clientWidth || 1, cssH = this.canvasTarget.clientHeight || 1
        const w = this.viewW || cssW / (this.scale || 1), h = this.viewH || cssH / (this.scale || 1)
        return { x: this.cam.x, y: this.cam.y, w, h }
    }

    // Let go of the chunks least recently in view, past the budget.
    #evictChunks() {
        const max = this.constructor.MAX_CHUNKS
        if (this.chunks.size <= max) return
        const view = this.#viewRect()
        const inView = new Set(this.#chunksIn(view.x, view.y, view.w, view.h).map((c) => c.key))
        const spare = [...this.chunks.values()].filter((c) => !inView.has(c.key)).sort((a, b) => a.last - b.last)
        while (this.chunks.size > max && spare.length) this.chunks.delete(spare.shift().key)
    }

    // --- the ground, sharp up close ------------------------------------------------
    // Zoomed in on a retina screen even a chunk would look soft. Past that
    // point the ground in view is painted again, as 512-unit tiles at the
    // resolution the screen needs. Tiles are painted in idle moments, nearest
    // first, and kept in a small cache; until one is ready the chunk stands in.

    static TILE = 512
    static MAX_TILES = 20

    #drawGround(ctx, viewW, viewH) {
        const W = this.world.W, H = this.world.H
        const gx = Math.max(0, this.cam.x), gy = Math.max(0, this.cam.y)
        const gw = Math.min(W - gx, viewW + 2), gh = Math.min(H - gy, viewH + 2)
        if (gw <= 0 || gh <= 0) return
        // whatever of a layer is painted within a rectangle of the world, one
        // drawImage per run of painted strips. `pad` reaches into the layer's
        // margin, so neighbouring chunks overlap.
        const blit = (layer, x, y, w, h, pad = 0) => {
            const S = this.constructor.STRIP, k = layer.k, m = layer.m
            const x0 = Math.max(x - pad, layer.x - m), x1 = Math.min(x + w + pad, layer.x + layer.w + m)
            if (x1 <= x0) return
            let i = 0
            while (i < layer.strips.length) {
                if (!layer.strips[i]) { i++; continue }
                let j = i
                while (j + 1 < layer.strips.length && layer.strips[j + 1]) j++
                const top = layer.y + (i === 0 ? -m : i * S), bottom = layer.y + (j === layer.strips.length - 1 ? layer.h + m : (j + 1) * S)
                const y0 = Math.max(y - pad, top), y1 = Math.min(y + h + pad, bottom)
                if (y1 > y0) ctx.drawImage(layer.canvas, (x0 - layer.x + m) * k, (y0 - layer.y + m) * k, (x1 - x0) * k, (y1 - y0) * k, x0, y0, x1 - x0, y1 - y0)
                i = j + 1
            }
        }
        const need = this.scale * (this.dpr || 1)
        let pending = false
        if (need <= this.overview.k * 1.15) {
            blit(this.overview, gx, gy, gw, gh)
            pending = !this.overview.done
        } else {
            const now = performance.now()
            this.#chunksIn(gx, gy, gw, gh).forEach((chunk) => {
                chunk.last = now
                const x = Math.max(gx, chunk.x), y = Math.max(gy, chunk.y)
                const w = Math.min(gx + gw, chunk.x + chunk.w) - x, h = Math.min(gy + gh, chunk.y + chunk.h) - y
                if (!chunk.done) { blit(this.overview, x, y, w, h); pending = true }
                blit(chunk, x, y, w, h, 1)
            })
        }
        if (pending) this.#scheduleGround()

        if (need <= this.groundK * 1.15) return
        const k = Math.min(3, Math.ceil(need * 2) / 2) // in half steps, so a small zoom reuses tiles
        const T = this.constructor.TILE
        const now = performance.now()
        for (let ty = Math.floor(gy / T) * T; ty < gy + gh; ty += T) {
            for (let tx = Math.floor(gx / T) * T; tx < gx + gw; tx += T) {
                const key = `${tx},${ty}`
                const tile = this.groundTiles.get(key)
                if (tile) { tile.last = now; ctx.drawImage(tile.canvas, tx - 1, ty - 1, T + 2, T + 2) }
                if (!tile || tile.k < k - 0.01) this.#queueTile(key, tx, ty, k)
            }
        }
    }

    #queueTile(key, tx, ty, k) {
        if (this.tileQueue.has(key)) return
        this.tileQueue.add(key)
        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 16))
        idle(() => {
            this.tileQueue.delete(key)
            if (!this.element.isConnected) return
            const T = this.constructor.TILE
            // still wanted? (you may have walked or zoomed on)
            const cx = this.cam.x + this.viewW / 2, cy = this.cam.y + this.viewH / 2
            if (Math.abs(tx + T / 2 - cx) > this.viewW + T || Math.abs(ty + T / 2 - cy) > this.viewH + T) return
            const canvas = document.createElement("canvas")
            canvas.width = Math.ceil((T + 2) * k)
            canvas.height = Math.ceil((T + 2) * k)
            const g = canvas.getContext("2d")
            g.setTransform(k, 0, 0, k, -(tx - 1) * k, -(ty - 1) * k)
            g.beginPath(); g.rect(tx - 1, ty - 1, T + 2, T + 2); g.clip()
            this.world.paintGround(g, this.palette, { x: tx - 8, y: ty - 8, w: T + 16, h: T + 16 })
            this.groundTiles.set(key, { canvas, k, last: performance.now() })
            if (this.groundTiles.size > this.constructor.MAX_TILES) {
                const oldest = [...this.groundTiles.entries()].sort((a, b) => a[1].last - b[1].last)[0]
                this.groundTiles.delete(oldest[0])
            }
        })
    }

    // In idle moments, render the sprites we'll need (nearest first) and
    // build the pathfinding grid, so walking somewhere new never stalls.
    #warmUp() {
        const token = (this.warmToken = (this.warmToken || 0) + 1)
        // only the neighbourhood: far sprites are drawn when you get there
        const order = this.world.sprites.map((sp, i) => [i, Math.hypot(sp.x - this.me.x, sp.y - this.me.y)]).filter(([, d]) => d < 2600).sort((a, b) => a[1] - b[1]).map(([i]) => i)
        let next = 0, gridDone = false
        const idle = window.requestIdleCallback || ((fn) => setTimeout(() => fn({ timeRemaining: () => 8 }), 32))
        const slice = (deadline) => {
            if (token !== this.warmToken || !this.element.isConnected) return
            while (deadline.timeRemaining() > 2 && next < order.length) {
                const i = order[next++]
                if (!this.spriteCanvases[i]) this.spriteCanvases[i] = renderSprite(this.world.sprites[i], this.palette, this.spriteK)
            }
            if (!gridDone && deadline.timeRemaining() > 2) gridDone = this.world.prepareGrid(Math.min(6, deadline.timeRemaining() - 1))
            if (next < order.length || !gridDone) idle(slice)
        }
        idle(slice)
    }

    // Let go of sprite drawings far from view, so a big map never holds them all.
    #evictFarSprites(viewW, viewH) {
        const cx = this.cam.x + viewW / 2, cy = this.cam.y + viewH / 2
        const keep = Math.max(viewW, viewH) * 1.2 + 1600
        this.world.sprites.forEach((sp, i) => {
            if (this.spriteCanvases[i] && Math.hypot(sp.x - cx, sp.y - cy) > keep) this.spriteCanvases[i] = undefined
        })
        this.staleSprites = null
    }

    // --- the minimap ------------------------------------------------------------
    // The whole map in the corner: the ground in miniature, every place as a
    // dot, people, you, and the part you're looking at. Click it to walk
    // there (pathfinding does the rest).

    #paintMinimapBase() {
        if (!this.hasMinimapTarget) return
        const canvas = this.minimapTarget
        canvas.style.aspectRatio = `${this.world.W} / ${this.world.H}`
        // the minimap's size follows the world's shape (see --mm-ratio in CSS)
        this.element.style.setProperty("--mm-ratio", (this.world.W / this.world.H).toFixed(3))
        const cssW = canvas.clientWidth || 180, cssH = canvas.clientHeight || Math.round(180 * this.world.H / this.world.W)
        const dpr = this.dpr || 1
        canvas.width = Math.round(cssW * dpr)
        canvas.height = Math.round(cssH * dpr)
        this.minimapBase = document.createElement("canvas")
        this.minimapBase.width = canvas.width
        this.minimapBase.height = canvas.height
        const g = this.minimapBase.getContext("2d")
        g.drawImage(this.overview.canvas, 0, 0, canvas.width, canvas.height)
        const sx = canvas.width / this.world.W, sy = canvas.height / this.world.H
        // the railway and the cable car, so the minimap shows how to get about
        this.transport.tracks.forEach((track) => {
            if (track.back || track.kind === "balloon") return
            const path = track.center
            g.save(); g.strokeStyle = "rgba(60,50,45,0.75)"; g.lineWidth = 1.4 * dpr
            if (track.kind === "cable") g.setLineDash([2 * dpr, 2 * dpr])
            g.beginPath()
            for (let d = 0; d <= path.L; d += 40) { const p = path.at(d); d ? g.lineTo(p.x * sx, p.y * sy) : g.moveTo(p.x * sx, p.y * sy) }
            if (path.closed) g.closePath()
            g.stroke(); g.restore()
        })
        this.config.landmarks.forEach((l) => {
            g.fillStyle = l.href || l.app ? "#E0A43A" : "#FFFBF0"
            g.strokeStyle = "rgba(30,40,40,0.8)"; g.lineWidth = 1 * dpr
            g.beginPath(); g.arc(l.x * sx, l.y * sy, 2.6 * dpr, 0, Math.PI * 2); g.fill(); g.stroke()
        })
    }

    #drawMinimap(viewW, viewH) {
        if (!this.hasMinimapTarget || !this.minimapBase || this.minimapTarget.offsetParent === null) return
        const canvas = this.minimapTarget, g = canvas.getContext("2d"), dpr = this.dpr || 1
        const sx = canvas.width / this.world.W, sy = canvas.height / this.world.H
        g.drawImage(this.minimapBase, 0, 0)
        g.strokeStyle = "rgba(255,255,255,0.9)"; g.lineWidth = 1.5 * dpr
        g.strokeRect(this.cam.x * sx, this.cam.y * sy, viewW * sx, viewH * sy)
        const dot = (p, color, r) => { g.fillStyle = color; g.beginPath(); g.arc(p.x * sx, p.y * sy, r * dpr, 0, Math.PI * 2); g.fill() }
        const T = this.#now()
        this.transport.tracks.forEach((track) => {
            if (track.kind !== "train") return
            for (let i = 0; i < track.count; i++) dot(this.transport.cars(track, i, T)[0], "#2C4F40", 2.2)
        })
        this.residents.forEach((p) => dot(p, p.tone, 2.4))
        this.#visiblePeople().forEach((p) => dot(p, "#4E7FB0", 2.6))
        dot(this.me, "#FFFFFF", 4)
        dot(this.me, "#E0A43A", 3)
        if (this.me.path?.length) {
            const end = this.me.path[this.me.path.length - 1]
            g.strokeStyle = "#E0A43A"; g.lineWidth = 1.5 * dpr
            g.beginPath(); g.arc(end.x * sx, end.y * sy, 4 * dpr, 0, Math.PI * 2); g.stroke()
        }
    }

    minimapClick(event) {
        const rect = this.minimapTarget.getBoundingClientRect()
        const x = ((event.clientX - rect.left) / rect.width) * this.world.W
        const y = ((event.clientY - rect.top) / rect.height) * this.world.H
        this.#resumeSoundIfWanted()
        this.#goTo(x, y)
        this.canvasTarget.focus({ preventScroll: true })
    }

    // Walk to (x, y) by the roads and around things, not in a straight line
    // into the first tree.
    #goTo(x, y, { keepTrip = false } = {}) {
        if (this.trip?.state === "riding") return
        if (this.trip && !keepTrip) this.#cancelTrip()
        const path = this.world.findPath(this.me, { x, y })
        this.me.path = path && path.length ? path : null
        if (this.me.path) { const next = this.me.path.shift(); this.me.tx = next.x; this.me.ty = next.y }
        else { this.me.tx = x; this.me.ty = y }
    }

    #now() {
        return Date.now() / 1000 + this.clockOffset
    }

    // If where we were saved is now inside something (a new building), move
    // to the nearest open ground.
    #unstick() {
        if (!this.world.blocked(this.me.x, this.me.y)) return
        for (let r = 20; r < 400; r += 20) {
            for (let k = 0; k < 16; k++) {
                const a = (k / 16) * Math.PI * 2, x = this.me.x + Math.cos(a) * r, y = this.me.y + Math.sin(a) * r
                if (!this.world.blocked(x, y)) { this.me.x = x; this.me.y = y; return }
            }
        }
    }

    // --- the loop ----------------------------------------------------------------

    #frame(t) {
        this.raf = requestAnimationFrame((next) => this.#frame(next))
        // never backwards: the first frame's timestamp can predate connect()
        const dt = Math.max(0, Math.min(0.05, (t - this.lastT) / 1000))
        this.lastT = t
        this.clock += dt
        const cssW = this.canvasTarget.clientWidth, cssH = this.canvasTarget.clientHeight
        if (!cssW || !cssH || !this.scale) return

        this.#ride(dt)
        this.#walk(dt)
        this.#placeResidents()
        this.#easeOthers(dt)
        this.#updatePlace()
        this.#mixSound()
        this.onStageId = this.#stageSpeaker()
        this.voiceTick += dt
        if (this.voiceTick > 0.25) { this.voiceTick = 0; this.#updateVoice() }
        this.peopleTick = (this.peopleTick || 0) + dt
        if (this.peopleTick > 1 && this.hasPeoplePanelTarget && !this.peoplePanelTarget.hidden) { this.peopleTick = 0; this.#renderPeople() }

        const viewW = cssW / this.scale, viewH = cssH / this.scale
        // The camera eases after you (a touch ahead of where you're walking)
        // rather than snapping, and snaps only on the first frame.
        const lead = this.moving ? 40 : 0
        const aimX = this.me.x + (this.me.tx !== null ? Math.sign(this.me.tx - this.me.x) * lead : 0)
        // up in a cabin or a balloon, look at it, not at its shadow
        const aimY = this.me.y - (this.me.seat?.lift || 0) * 0.85 + (this.me.ty !== null && this.me.tx !== null ? Math.sign(this.me.ty - this.me.y) * lead * 0.6 : 0)
        const targetX = viewW >= this.world.W ? (this.world.W - viewW) / 2 : Math.max(0, Math.min(this.world.W - viewW, aimX - viewW / 2))
        const targetY = viewH >= this.world.H ? (this.world.H - viewH) / 2 : Math.max(0, Math.min(this.world.H - viewH, aimY - viewH / 2))
        const ease = !this.camReady || this.reduceMotion ? 1 : Math.min(1, dt * 5)
        this.cam.x += (targetX - this.cam.x) * ease
        this.cam.y += (targetY - this.cam.y) * ease
        this.camReady = true
        this.viewW = viewW
        this.viewH = viewH

        const ctx = this.ctx, pal = this.palette, s = this.scale * this.dpr
        // Start every frame from a clean context. If anything ever throws
        // between a save() and its restore(), the leftover clip would
        // otherwise hide the rest of the map for the rest of the visit.
        if (ctx.reset) ctx.reset()
        const now = new Date()
        const hour = window.__squareHour ?? now.getHours() + now.getMinutes() / 60
        const light = this.ambient.light(pal.night, hour)
        this.light = light
        this.ambient.setView({ x: this.cam.x, y: this.cam.y, w: viewW, h: viewH })
        this.ambient.update(dt, { night: pal.night, day: light.day })
        this.transport.update(dt, this.#now(), { x: this.cam.x, y: this.cam.y, w: viewW, h: viewH }, this.reduceMotion)
        this.#animateWalkers(dt)
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        ctx.fillStyle = pal.groundEdge
        ctx.fillRect(0, 0, this.canvasTarget.width, this.canvasTarget.height)
        ctx.setTransform(s, 0, 0, s, -this.cam.x * s, -this.cam.y * s)
        this.#drawGround(ctx, viewW, viewH)

        this.world.lilies.forEach((pad, i) => paintLily(ctx, pal, pad, this.ambient.lilyBob(i)))
        this.ambient.drawWater(ctx, pal, light)
        this.#drawPlaceHighlight(ctx, pal)
        this.#drawNotes(ctx, pal)
        this.#drawHearing(ctx, pal)
        this.ambient.drawGroundLife(ctx, pal)
        this.#drawScene(ctx, pal, viewW, viewH)
        this.transport.drawSmoke(ctx, pal)
        this.transport.drawAir(ctx, pal, this.#now(), { x: this.cam.x, y: this.cam.y, w: viewW, h: viewH }, this.riders, this.drawRider)
        this.ambient.drawAir(ctx, pal, light)
        this.ambient.drawClouds(ctx, light)
        this.ambient.drawTint(ctx, light, { x: this.cam.x, y: this.cam.y, w: viewW, h: viewH })
        this.#drawGlows(ctx, pal)
        this.ambient.drawLights(ctx, pal, light)
        this.transport.drawLights(ctx, pal, this.#now(), { x: this.cam.x, y: this.cam.y, w: viewW, h: viewH }, this.#glowSprite(), light)
        this.#drawLabels(ctx, pal, viewW, viewH)
        this.#drawBubbles(ctx, pal)
        this.minimapTick = (this.minimapTick || 0) + dt
        if (this.minimapTick > 0.12) { this.minimapTick = 0; this.#drawMinimap(viewW, viewH) }
        this.evictTick = (this.evictTick || 0) + dt
        if (this.evictTick > 3) { this.evictTick = 0; this.#evictFarSprites(viewW, viewH) }
    }

    // Everything upright, back to front by where it meets the ground, so a
    // walker north of a tree disappears behind it and one south of it stands
    // in front. Sprites off-screen are skipped.
    #drawScene(ctx, pal, viewW, viewH) {
        const margin = 80
        const left = this.cam.x - margin, right = this.cam.x + viewW + margin
        const top = this.cam.y - margin, bottom = this.cam.y + viewH + margin * 2
        const items = []
        let rendered = 0
        this.world.sprites.forEach((sprite, i) => {
            if (sprite.x + sprite.box[2] < left || sprite.x + sprite.box[0] > right) return
            if (sprite.y + sprite.box[3] < top || sprite.y + sprite.box[1] > bottom) return
            if (!this.spriteCanvases[i] && this.staleSprites?.[i]) {
                // just zoomed: the old drawing stands in until the sharp one is ready
                items.push({ y: sprite.y, sprite, canvas: this.staleSprites[i] })
                return
            }
            if (!this.spriteCanvases[i]) {
                // not warmed up yet: render a few now, the rest next frame
                // (more while riding, when the view is always somewhere new)
                if (rendered >= (this.trip?.state === "riding" ? 12 : 4)) return
                this.spriteCanvases[i] = renderSprite(sprite, this.palette, this.spriteK || 1)
                rendered++
            }
            items.push({ y: sprite.y, sprite, canvas: this.spriteCanvases[i] })
        })
        // riders are drawn by their vehicle (see transport.js), everyone else here
        this.riders = new Map()
        const aboard = (p) => {
            if (!p.seat) return false
            if (!this.riders.has(p.seat.vehicle)) this.riders.set(p.seat.vehicle, [])
            this.riders.get(p.seat.vehicle).push(p)
            return true
        }
        ;[...this.residents, ...this.#visiblePeople(), this.me].forEach((token) => { if (!aboard(token)) items.push({ y: token.y + 16, token }) })
        const T = this.#now()
        this.transport.sceneItems(T, { x: this.cam.x, y: this.cam.y, w: viewW, h: viewH }, this.riders, this.drawRider).forEach((item) => items.push({ y: item.y, part: item }))
        const fire = this.world.byKey.campfire
        if (fire) items.push({ y: fire.y + 4, flame: fire })
        const caveFire = this.world.anchors.caveFire
        if (caveFire) items.push({ y: caveFire.y + 4, flame: { x: caveFire.x, y: caveFire.y - 2 } })
        this.ambient.sceneItems(this.light).forEach((part) => items.push({ y: part.y, part }))
        items.sort((a, b) => a.y - b.y)
        items.forEach((item) => {
            if (item.sprite) {
                const [l, t, r, b] = item.sprite.box
                const sway = item.sprite.sway ? this.ambient.wind(item.sprite.x, item.sprite.y) * item.sprite.sway : 0
                if (sway) {
                    // bend from the base: the roots stay put, the crown moves
                    ctx.save()
                    ctx.translate(item.sprite.x, item.sprite.y)
                    ctx.transform(1, 0, -sway, 1, 0, 0)
                    ctx.drawImage(item.canvas, l, t, r - l, b - t)
                    ctx.restore()
                } else {
                    ctx.drawImage(item.canvas, item.sprite.x + l, item.sprite.y + t, r - l, b - t)
                }
            } else if (item.token) {
                this.#drawToken(ctx, pal, item.token)
            } else if (item.part) {
                item.part.draw(ctx, pal)
            } else {
                this.#drawFlame(ctx, pal, item.flame)
            }
        })
    }

    #walk(dt) {
        const me = this.me
        if (this.trip?.state === "riding") return
        let vx = 0, vy = 0
        if (this.keys.has("arrowleft") || this.keys.has("a")) vx -= 1
        if (this.keys.has("arrowright") || this.keys.has("d")) vx += 1
        if (this.keys.has("arrowup") || this.keys.has("w")) vy -= 1
        if (this.keys.has("arrowdown") || this.keys.has("s")) vy += 1
        if (me.tx !== null) {
            const dx = me.tx - me.x, dy = me.ty - me.y, d = Math.hypot(dx, dy)
            if (d < 6) {
                // next waypoint on the way, if we're following a path
                if (me.path && me.path.length) { const next = me.path.shift(); me.tx = next.x; me.ty = next.y }
                else { me.tx = null; me.path = null }
            } else { vx = dx / d; vy = dy / d }
        }
        const len = Math.hypot(vx, vy)
        if (!len) {
            if (this.moving) { this.moving = false; this.#sendPosition(true) }
            return
        }
        // a long walk runs by itself; Shift runs on purpose
        const longWalk = me.path && me.path.length > 3
        const veryLong = me.path && me.path.length > 9
        const step = (veryLong ? 680 : this.running || longWalk ? 430 : 260) * dt
        vx = (vx / len) * step; vy = (vy / len) * step
        const blocked = this.world.blocked
        let moved = false
        if (!blocked(me.x + vx, me.y + vy)) { me.x += vx; me.y += vy; moved = true }
        else if (!blocked(me.x + vx, me.y)) { me.x += vx; moved = true }
        else if (!blocked(me.x, me.y + vy)) { me.y += vy; moved = true }
        if (!moved) { me.tx = null; me.path = null; return }
        this.moving = true
        this.movedYet = true
        this.#sendPosition(false)
    }

    #sendPosition(force) {
        const now = performance.now()
        if (!force && now - this.lastSentAt < 125) return
        const x = Math.round(this.me.x), y = Math.round(this.me.y)
        const ride = this.trip?.state === "riding" ? this.trip.tag : null
        if (this.lastSent && this.lastSent[0] === x && this.lastSent[1] === y && this.lastSent[2] === ride) return
        this.lastSentAt = now
        this.lastSent = [x, y, ride]
        this.#send("move", ride ? { x, y, ride } : { x, y })
        this.#publishPosition()
    }

    // Walkers bob as they step and kick up a little dust; standing still,
    // they rest. The bob is driven by distance covered, so faster is bouncier.
    #animateWalkers(dt) {
        const tokens = [this.me, ...this.people.values(), ...this.residents]
        this.dustClock = (this.dustClock || 0) + dt
        const dustNow = this.dustClock > 0.14
        if (dustNow) this.dustClock = 0
        tokens.forEach((p) => {
            const last = p.lastPos || [p.x, p.y]
            const moved = Math.hypot(p.x - last[0], p.y - last[1])
            p.lastPos = [p.x, p.y]
            p.walking = moved > 0.3 && !p.seat
            p.walkPhase = (p.walkPhase || 0) + moved * 0.18
            const target = p.walking && !this.reduceMotion ? -Math.abs(Math.sin(p.walkPhase)) * 3.4 : (p.resident && !this.reduceMotion ? Math.sin(this.clock * 2 + (p.key === "jane" ? 0 : 1)) * 0.8 : 0)
            p.bob = target
            if (p.walking && dustNow) this.ambient.step(p.x, p.y)
        })
    }

    #placeResidents() {
        const now = this.#now()
        this.residents.forEach((r) => {
            if (!r.loop) return
            const angle = 2 * Math.PI * ((now % r.loop.period) / r.loop.period)
            r.x = r.loop.x + r.loop.rx * Math.cos(angle)
            r.y = r.loop.y + r.loop.ry * Math.sin(angle)
        })
    }

    #easeOthers(dt) {
        const k = Math.min(1, dt * 10)
        const T = this.#now()
        this.people.forEach((p) => {
            // a rider sits in their vehicle, wherever the clock puts it
            const seat = p.ride ? this.transport.seat(p.ride, p.id, T) : null
            p.seat = seat
            if (seat) { p.x = seat.x; p.y = seat.y; return }
            p.x += (p.gx - p.x) * k
            p.y += (p.gy - p.y) * k
        })
    }

    #onStage(p) {
        return !!this.stageSpot && Math.hypot(p.x - this.stageSpot.x, p.y - this.stageSpot.y) < this.stageSpot.r
    }

    // Who (other than us) is standing on the stage right now, if anyone.
    #stageSpeaker() {
        if (!this.stageSpot) return null
        for (const p of this.people.values()) if (this.#onStage(p)) return p.id
        return null
    }

    // The noticeboard's place card lists what is pinned there.
    #renderBoard() {
        if (!this.hasPlaceBlurbTarget) return
        const place = this.config.landmarks.find((l) => l.key === this.currentPlace)
        if (!this.boardNotes.length) { this.placeBlurbTarget.textContent = place.blurb; return }
        this.placeBlurbTarget.textContent = this.boardNotes.slice(0, 3).map((n) => `${n.name}: “${n.text}” (${this.#ago(n.at)})`).join("  ·  ")
    }

    #updatePlace() {
        if (this.trip) return // the trip owns the card
        const place = this.config.landmarks.find((l) => Math.hypot(l.x - this.me.x, l.y - this.me.y) < l.r) || null
        const key = place ? place.key : null
        const ground = place ? null : this.#openGround()[0]
        if (key === this.currentPlace && ground === this.currentGround) return
        this.currentPlace = key
        this.currentGround = ground
        if (!this.hasPlaceNameTarget) return
        if (place) {
            this.placeNameTarget.textContent = place.label
            this.placeBlurbTarget.textContent = place.blurb
            if (place.pin_days) this.#renderBoard()
            if (place.href) {
                this.placeActionTarget.href = place.href
                this.placeActionTarget.textContent = place.action || "Open"
                this.placeActionTarget.hidden = false
            } else {
                this.placeActionTarget.hidden = true
            }
        } else {
            const [name, blurb] = this.#openGround()
            this.placeNameTarget.textContent = name
            this.placeBlurbTarget.textContent = blurb
            this.placeActionTarget.hidden = true
        }
        this.#renderWant(place)
        this.#renderRides(place)
    }

    // --- getting around ---------------------------------------------------------
    // A station's card offers its line's other stops. Pick one: walk to the
    // platform, wait for the next train (the clock says when), ride, and step
    // off at the other end. While you ride, your moves carry the vehicle's
    // tag, so everyone else seats you in the same train.

    static KIND_WORDS = {
        train: { vehicle: "train", pick: "Take the train to…", go: "Take the train to", riding: (line) => `On ${line.replace(/^The /, "the ")}` },
        cable: { vehicle: "cabin", pick: "Ride the cable car to…", go: "Ride up to", riding: () => "In the cable car" },
        balloon: { vehicle: "balloon", pick: "Fly to…", go: "Fly to", riding: () => "Up in a balloon" }
    }

    // A station's name as it reads mid-sentence: "the summit station".
    static inSentence(label) {
        return String(label || "").replace(/^Cable car, /, "the ").replace(/^The /, "the ")
    }

    #destinations(place) {
        const station = place && this.transport.stations[place.key]
        if (!station) return []
        return Object.values(this.transport.stations).filter((s) => s.line === station.line && s.key !== place.key)
    }

    #renderRides(place) {
        if (!this.hasPlaceRideTarget || !this.hasPlaceTripTarget) return
        const dest = this.#destinations(place)
        const words = place && this.constructor.KIND_WORDS[this.transport.stations[place.key]?.kind]
        this.placeRideTarget.hidden = dest.length < 2
        this.placeTripTarget.hidden = dest.length !== 1
        if (!dest.length) return
        if (dest.length === 1) {
            const go = place.key === "cable_summit" ? "Ride down to" : words.go
            this.placeTripTarget.textContent = `${go} ${this.constructor.inSentence(dest[0].label)}`
            this.placeTripTarget.dataset.to = dest[0].key
            return
        }
        const first = document.createElement("option")
        first.value = ""; first.textContent = words.pick; first.disabled = true; first.selected = true
        const opts = dest.map((d) => {
            const o = document.createElement("option")
            o.value = d.key; o.textContent = d.label
            return o
        })
        this.placeRideTarget.replaceChildren(first, ...opts)
        this.placeRideTarget.setAttribute("aria-label", words.pick.replace("…", ""))
    }

    rideChosen(e) {
        const to = e.target.value
        if (to && this.currentPlace) this.#startTrip(this.currentPlace, to)
        this.#backToMap()
    }

    // The card's one button: go (a two-stop line), not now (waiting), or get
    // off at the next stop (riding the Loop).
    tripButton() {
        const trip = this.trip
        if (!trip) { if (this.currentPlace && this.placeTripTarget.dataset.to) this.#startTrip(this.currentPlace, this.placeTripTarget.dataset.to) }
        else if (trip.state === "waiting") this.#cancelTrip()
        else if (trip.state === "riding" && this.transport.byKey[trip.track].def.loop) {
            const st = this.transport.state(this.transport.byKey[trip.track], trip.vehicle, this.#now())
            trip.to = st.stop >= 0 && st.left > 0.5 ? st.stop : st.next
            trip.toKey = this.transport.byKey[trip.track].stops[trip.to].key
            this.#renderTrip()
        }
        this.#backToMap()
    }

    #startTrip(fromKey, toKey) {
        const plan = this.transport.plan(fromKey, toKey)
        if (!plan) return
        const from = this.world.byKey[fromKey]
        this.trip = { ...plan, fromKey, toKey, state: "waiting", tag: null, vehicle: -1 }
        this.keys.clear()
        if (Math.hypot(this.me.x - from.x, this.me.y - from.y) > 30) this.#goTo(from.x, from.y, { keepTrip: true })
        this.#renderTrip()
    }

    #cancelTrip() {
        this.trip = null
        this.me.seat = null
        this.currentPlace = undefined
        this.#updatePlace()
    }

    #ride(dt) {
        const trip = this.trip
        if (!trip) return
        const T = this.#now()
        const track = this.transport.byKey[trip.track]
        if (!track) { this.#cancelTrip(); return }
        if (trip.state === "waiting") {
            const from = this.world.byKey[trip.fromKey]
            if (this.me.tx === null && Math.hypot(this.me.x - from.x, this.me.y - from.y) < 70) {
                const next = this.transport.nextDeparture(trip.track, trip.from, trip.dir, T)
                if (next.wait === 0) {
                    trip.state = "riding"; trip.vehicle = next.i; trip.tag = `${trip.track}:${next.i}`
                    this.me.path = null
                    this.#renderTrip()
                }
            }
        }
        if (trip.state === "riding") {
            const seat = this.transport.seat(trip.tag, this.me.id, T)
            if (!seat) { this.#cancelTrip(); return }
            this.me.x = seat.x; this.me.y = seat.y; this.me.seat = seat
            this.movedYet = true
            const st = this.transport.state(track, trip.vehicle, T)
            if (st.stop === trip.to) { this.#alight(); return }
            this.#sendPosition(false)
            // the view is always somewhere new: draw the neighbourhood ahead
            this.rideWarm = (this.rideWarm || 0) + dt
            if (this.rideWarm > 1.2) { this.rideWarm = 0; this.#warmUp() }
        }
        this.tripTick = (this.tripTick || 0) + dt
        if (this.tripTick > 0.25) { this.tripTick = 0; this.#renderTrip() }
    }

    // Off at the other end, onto the platform.
    #alight() {
        const to = this.world.byKey[this.trip.toKey]
        const x = to.x + (Math.random() - 0.5) * 30, y = to.y + (Math.random() - 0.5) * 14
        const free = !this.world.blocked(x, y)
        this.me.x = free ? x : to.x; this.me.y = free ? y : to.y
        this.me.tx = null; this.me.path = null
        this.trip = null
        this.me.seat = null
        this.#sendPosition(true)
        this.ambient.arrive(this.me.x, this.me.y)
        this.#warmUp()
        this.currentPlace = undefined
        this.#updatePlace()
    }

    #renderTrip() {
        const trip = this.trip
        if (!trip || !this.hasPlaceNameTarget) return
        const track = this.transport.byKey[trip.track]
        const words = this.constructor.KIND_WORDS[track.kind]
        const label = (i) => this.world.byKey[track.stops[i].key]?.label || ""
        const to = this.constructor.inSentence(this.world.byKey[trip.toKey].label)
        this.placeActionTarget.hidden = true
        if (this.hasPlaceWantTarget) this.placeWantTarget.hidden = true
        if (this.hasPlaceRideTarget) this.placeRideTarget.hidden = true
        if (trip.state === "waiting") {
            const next = this.transport.nextDeparture(trip.track, trip.from, trip.dir, this.#now())
            this.placeNameTarget.textContent = track.name
            const walking = this.me.tx !== null
            const due = next.wait < 1.5 ? `the ${words.vehicle} is in` : `the next ${words.vehicle} is due in ${Math.ceil(next.wait)}s`
            this.placeBlurbTarget.textContent = walking ? `To ${to}. Walking to the platform.` : `To ${to}: ${due}.`
            this.placeTripTarget.textContent = "Not now"
            this.placeTripTarget.hidden = false
        } else {
            const st = this.transport.state(track, trip.vehicle, this.#now())
            this.placeNameTarget.textContent = words.riding(track.name)
            const stopped = st.stop >= 0 && st.stop !== trip.to
            this.placeBlurbTarget.textContent = track.def.loop
                ? (stopped ? `Stopped at ${label(st.stop)}. You get off at ${to}.` : st.next === trip.to ? `Next stop: ${to}, where you get off.` : `Next stop: ${label(st.next)}. You get off at ${to}.`)
                : `On the way to ${to}.`
            const offNext = track.def.loop && (stopped ? st.stop : st.next) !== trip.to && !(stopped && st.stop === trip.from)
            this.placeTripTarget.textContent = stopped ? "Get off here" : "Get off at the next stop"
            this.placeTripTarget.hidden = !offNext
        }
    }

    // Open ground names itself by where you are.
    #openGround() {
        if (this.world.onFerry(this.me.x, this.me.y)) return ["On the ferry", "The boat rocks gently on the swell. Click an island to land on it."]
        const biome = this.world.biome ? this.world.biome(this.me.x, this.me.y) : "meadow"
        if (biome === "birch") return ["The birch edge", "Heather and birches, where the meadow runs out and the north begins."]
        if (biome === "snow") return ["The snowfields", "Snow as far as the lanterns. After dark, look up for the northern lights."]
        if (biome === "alpine") return ["The High Peaks", "Alpine meadows, gentians and cold clear lakes. The trails wind through the passes."]
        if (biome === "crag" || biome === "peak-snow") return ["The High Peaks", "Scree and snow under the summits. Follow the trail through the pass."]
        if (biome === "tropical") return ["The Warm Isles", "Palms, white sand and warm shallows. The ferries run between the islands."]
        if (biome === "oasis") return ["The Dunes", "A spring in the sand, ringed with palms."]
        if (biome === "canyon") return ["The Dunes", "The river's red canyon, cut down through the rock."]
        if (biome === "desert") return ["The Dunes", "Gravel plain and acacias, and further south a sea of dunes."]
        if (biome === "rainforest") return ["The Wildwood", "Giant trees, ferns and vines. Somewhere upriver, the falls."]
        return ["The clearing", "Walk up to someone. Their words sharpen as you get close and fade as you leave."]
    }

    // A coming-soon place offers "Want this?": one tap records it, and the
    // card says how many want it too.
    #renderWant(place) {
        if (!this.hasPlaceWantTarget) return
        if (!place || !place.soon) { this.placeWantTarget.hidden = true; return }
        const interest = (this.config.place_interest || {})[place.key] || { count: 0, mine: false }
        this.placeWantTarget.hidden = false
        this.placeWantTarget.disabled = interest.mine
        this.placeWantTarget.setAttribute("aria-pressed", interest.mine ? "true" : "false")
        const others = interest.count - (interest.mine ? 1 : 0)
        this.placeWantTarget.textContent = interest.mine ? (others > 0 ? `You and ${others} ${others === 1 ? "other" : "others"} want this` : "You want this") : (interest.count ? `Want this? (${interest.count})` : "Want this?")
        this.placeWantTarget.title = `${place.soon} is coming. Tap to say you'd use it.`
    }

    async wantPlace() {
        const place = this.config.landmarks.find((l) => l.key === this.currentPlace)
        if (!place || !place.soon || !this.config.place_interest_path) return
        const interest = (this.config.place_interest ||= {})[place.key] ||= { count: 0, mine: false }
        if (interest.mine) return
        interest.mine = true; interest.count += 1
        this.#renderWant(place)
        try {
            const response = await fetch(this.config.place_interest_path.replace("__key__", encodeURIComponent(place.key)), { method: "POST", headers: { "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "", Accept: "application/json" }, credentials: "same-origin" })
            if (response.ok) { const data = await response.json(); interest.count = data.count; this.#renderWant(place) }
        } catch (_error) { /* the count catches up next visit */ }
        this.#backToMap()
    }

    #falloff(d, full = this.full, zero = this.zero) {
        if (d <= full) return 1
        if (d >= zero) return 0
        const t = (d - full) / (zero - full)
        return 1 - t * t * (3 - 2 * t)
    }

    #mixSound() {
        if (!this.soundOn || !this.sounds) return
        if (Math.floor(this.clock) !== this.lastTrackCheck) { this.lastTrackCheck = Math.floor(this.clock); this.#syncSoundTracks() }
        this.sounds.forEach((s) => {
            const d = s.zone.river ? this.#distanceToRiver() : s.zone.sea ? this.#distanceToCoast() : s.zone.wind ? this.#windDistance() : s.zone.biomes ? (s.zone.biomes.includes(this.world.biome(this.me.x, this.me.y)) ? 0 : 600) : Math.hypot(s.zone.x - this.me.x, s.zone.y - this.me.y)
            const v = this.#falloff(d, 70, 480) ** 2 * 0.8 * (s.zone.gain || 1)
            s.audio.volume = Math.max(0, Math.min(1, v))
        })
    }

    // --- drawing -----------------------------------------------------------------

    #positionOf(speaker) {
        if (speaker === this.me.id) return this.me
        return this.residents.find((r) => r.id === speaker) || this.people.get(speaker) || null
    }

    // The ferry: a rowing boat under the token, rocking on the swell, with a
    // wake behind it while it moves.
    #drawBoat(ctx, pal, p) {
        const heading = p.walking && p.lastPos ? Math.atan2(p.y - p.lastPos[1], p.x - p.lastPos[0]) : (p.heading || 0)
        p.heading = heading
        const rock = this.reduceMotion ? 0 : Math.sin(this.clock * 2.2 + p.x * 0.01) * 0.06
        ctx.save()
        ctx.translate(p.x, p.y + 14)
        ctx.rotate(Math.cos(heading) >= 0 ? rock : Math.PI + rock)
        if (p.walking) {
            ctx.strokeStyle = "rgba(255,255,255,0.5)"; ctx.lineWidth = 2
            ctx.beginPath(); ctx.moveTo(-34, -6); ctx.quadraticCurveTo(-56, -4, -76, -14); ctx.moveTo(-34, 6); ctx.quadraticCurveTo(-56, 6, -76, 14); ctx.stroke()
        }
        ctx.beginPath(); ctx.moveTo(-34, -8); ctx.quadraticCurveTo(-30, 10, 0, 11); ctx.quadraticCurveTo(28, 10, 38, -2); ctx.quadraticCurveTo(28, -12, 0, -12); ctx.quadraticCurveTo(-26, -12, -34, -8); ctx.closePath()
        ctx.fillStyle = pal.falu || "#A8453A"; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.6; ctx.stroke()
        ctx.beginPath(); ctx.ellipse(0, -1, 28, 7, 0, 0, Math.PI * 2); ctx.fillStyle = pal.woodLight; ctx.fill(); ctx.stroke()
        ctx.strokeStyle = pal.woodDark; ctx.lineWidth = 3
        ctx.beginPath(); ctx.moveTo(-8, -18); ctx.lineTo(4, 16); ctx.stroke()
        ctx.restore()
    }

    // `at` draws the token somewhere other than where it stands: a rider, in
    // their seat (no shadow, no boat).
    #drawToken(ctx, pal, p, at = null) {
        const d = p.self ? 0 : Math.hypot(p.x - this.me.x, p.y - this.me.y)
        const alpha = p.self ? 1 : 0.28 + 0.72 * this.#falloff(d)
        // The token lifts as it steps (p.bob); its shadow stays on the ground
        // and shrinks a little under it.
        const lift = p.bob || 0
        // With a camera on, the token becomes a bigger video bubble that
        // rises off its spot (so its foot stays where it stands).
        const video = p.resident ? null : this.voice.videoFor(p.self ? "self" : p.id)
        const rad = video ? 30 : 17
        const x = at ? at.x : p.x, y = (at ? at.y : p.y) + lift - (rad - 17)
        ctx.save()
        ctx.globalAlpha = alpha
        // Out on a ferry lane, a walker rides in a little boat.
        if (at) { /* seated: the vehicle is under them */ } else if (this.world.onFerry(p.x, p.y)) this.#drawBoat(ctx, pal, p)
        else {
            ctx.fillStyle = `rgba(${pal.shadow},0.28)`
            ctx.beginPath(); ctx.ellipse(x + 3, p.y + 18, 15 + lift * 0.8, 5 + lift * 0.2, 0, 0, Math.PI * 2); ctx.fill()
        }

        const img = p.avatar ? this.#image(p.avatar) : null
        ctx.beginPath(); ctx.arc(x, y, rad, 0, Math.PI * 2)
        ctx.fillStyle = p.tone || this.#toneFor(p.id)
        ctx.fill()
        if (video) {
            // centre-crop the camera frame into the circle, mirrored for yourself
            const vw = video.videoWidth, vh = video.videoHeight, side = Math.min(vw, vh)
            ctx.save(); ctx.clip()
            if (p.self) { ctx.translate(x * 2, 0); ctx.scale(-1, 1) }
            ctx.drawImage(video, (vw - side) / 2, (vh - side) / 2, side, side, x - rad, y - rad, rad * 2, rad * 2)
            ctx.restore()
        } else if (img && img.complete && img.naturalWidth) {
            ctx.save(); ctx.clip(); ctx.drawImage(img, x - rad, y - rad, rad * 2, rad * 2); ctx.restore()
        } else {
            ctx.fillStyle = "#fff"
            ctx.font = "700 15px system-ui, sans-serif"
            ctx.textAlign = "center"; ctx.textBaseline = "middle"
            ctx.fillText(p.initial || "?", x, y + 1)
            ctx.textBaseline = "alphabetic"
        }
        const speaking = p.resident ? 0 : this.voice.level(p.self ? "self" : p.id)
        if (speaking > 0.08) {
            ctx.save()
            ctx.strokeStyle = "#4FBF7A"
            ctx.globalAlpha = alpha * Math.min(1, 0.35 + speaking)
            ctx.lineWidth = 3
            ctx.beginPath(); ctx.arc(x, y, rad + 4 + speaking * 7, 0, Math.PI * 2); ctx.stroke()
            ctx.restore()
        }
        if (!p.resident && (p.self ? this.voice.enabled : this.voice.isSpeaker(p.id))) {
            ctx.save()
            ctx.globalAlpha = alpha
            ctx.fillStyle = p.self && this.voice.muted ? "#C0624A" : "#3E9E66"
            const bx = x + rad * 0.76, by = y - rad * 0.7
            ctx.beginPath(); ctx.arc(bx, by, 6.5, 0, Math.PI * 2); ctx.fill()
            ctx.strokeStyle = pal.tagBg; ctx.lineWidth = 1.5; ctx.stroke()
            ctx.fillStyle = "#fff"
            ctx.fillRect(bx - 1.4, by - 3.5, 2.8, 5)
            ctx.fillRect(bx - 0.4, by + 2, 0.9, 1.8)
            ctx.restore()
        }
        ctx.lineWidth = p.self ? 3.5 : 2.5
        ctx.strokeStyle = p.self ? "#E0A43A" : pal.tagBg
        ctx.beginPath(); ctx.arc(x, y, rad, 0, Math.PI * 2); ctx.stroke()

        const label = p.self ? "You" : p.name
        ctx.font = "600 12px system-ui, sans-serif"
        const w = ctx.measureText(label).width + 12
        ctx.fillStyle = pal.tagBg
        ctx.globalAlpha = alpha * 0.92
        this.#roundRect(ctx, x - w / 2, y + rad + 5, w, 18, 9); ctx.fill()
        ctx.globalAlpha = alpha
        ctx.fillStyle = pal.label
        ctx.textAlign = "center"
        ctx.fillText(label, x, y + rad + 18)
        ctx.restore()
    }

    #drawBubbles(ctx, pal) {
        this.bubbles = this.bubbles.filter((b) => this.clock - b.born < b.life)
        this.bubbles.forEach((b) => {
            const who = this.#positionOf(b.speaker)
            const x = who ? who.x : b.x, y = who ? who.y : b.y
            const d = b.speaker === this.me.id ? 0 : Math.hypot(x - this.me.x, y - this.me.y)
            const age = this.clock - b.born
            const fade = Math.min(1, age / 0.25, (b.life - age) / 0.6)
            const a = (b.stage ? 1 : this.#falloff(d)) * fade
            const inView = x > this.cam.x && x < this.cam.x + this.viewW && y > this.cam.y && y < this.cam.y + this.viewH
            // A stage line from someone off-screen still reaches you, as a
            // banner across the top of the map.
            if (b.stage && !inView) { this.#bubble(ctx, pal, this.cam.x + this.viewW / 2, this.cam.y + 120, `${b.name} on stage: ${b.text}`, fade); return }
            if (a > 0.06) this.#bubble(ctx, pal, x, y + (who?.bob || 0), b.text, a, age)
            else if (fade > 0.2) this.#murmur(ctx, pal, x, y)
        })
    }

    #bubble(ctx, pal, x, y, text, alpha, age = 1) {
        ctx.save()
        // pop in with a little overshoot, from the tail
        if (!this.reduceMotion && age < 0.35) {
            const t = age / 0.35, c = 1.8
            const k = 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2)
            ctx.translate(x, y - 22)
            ctx.scale(Math.max(0.01, k), Math.max(0.01, k))
            ctx.translate(-x, -(y - 22))
        }
        ctx.globalAlpha = alpha
        ctx.font = "500 14px system-ui, sans-serif"
        const all = this.#wrap(ctx, text, 220)
        const lines = all.slice(0, 7)
        if (all.length > lines.length) lines[lines.length - 1] = `${lines[lines.length - 1]}…`
        const lh = 18
        const w = Math.max(...lines.map((l) => ctx.measureText(l).width)) + 22
        const h = lines.length * lh + 14
        const bx = Math.min(Math.max(x - w / 2, this.cam.x + 6), this.cam.x + this.viewW - w - 6)
        const by = y - 30 - h
        ctx.fillStyle = pal.tagBg
        this.#roundRect(ctx, bx, by, w, h, 10); ctx.fill()
        ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.3; ctx.globalAlpha = alpha * 0.7; ctx.stroke()
        ctx.globalAlpha = alpha
        ctx.beginPath(); ctx.moveTo(x - 6, by + h - 0.5); ctx.lineTo(x, by + h + 8); ctx.lineTo(x + 6, by + h - 0.5); ctx.fillStyle = pal.tagBg; ctx.fill()
        ctx.fillStyle = pal.label
        ctx.textAlign = "left"
        lines.forEach((l, i) => ctx.fillText(l, bx + 11, by + 19 + i * lh))
        ctx.restore()
    }

    #murmur(ctx, pal, x, y) {
        ctx.save()
        ctx.globalAlpha = 0.45
        ctx.fillStyle = pal.tagBg
        this.#roundRect(ctx, x - 18, y - 48, 36, 18, 9); ctx.fill()
        ctx.fillStyle = pal.label
        for (let i = 0; i < 3; i++) {
            const bob = this.reduceMotion ? 0 : Math.sin(this.clock * 5 + i) * 1.5
            ctx.beginPath(); ctx.arc(x - 8 + i * 8, y - 39 + bob, 2, 0, Math.PI * 2); ctx.fill()
        }
        ctx.restore()
    }

    // Lines from the last day, drawn as small pencil speech marks where they
    // were said. Standing on one shows what was said there, and when.
    #drawNotes(ctx, pal) {
        const live = new Set(this.bubbles.map((b) => b.text))
        // While a conversation is live within earshot, the marks stay quiet:
        // the bubbles (and the chat log) already say it.
        const talking = this.bubbles.some((b) => {
            const who = this.#positionOf(b.speaker) || b
            return Math.hypot(who.x - this.me.x, who.y - this.me.y) < this.zero
        })
        const now = this.#now()
        let nearest = null, nd = 70
        this.notes.forEach((n) => {
            if (live.has(n.text)) return
            ctx.save()
            ctx.globalAlpha = 0.5
            ctx.strokeStyle = pal.ink
            ctx.lineWidth = 1.2
            this.#roundRect(ctx, n.x - 7, n.y - 6, 14, 10, 4); ctx.stroke()
            ctx.beginPath(); ctx.moveTo(n.x - 2, n.y + 4); ctx.lineTo(n.x - 4, n.y + 8); ctx.lineTo(n.x + 2, n.y + 4); ctx.stroke()
            ctx.restore()
            const d = Math.hypot(n.x - this.me.x, n.y - this.me.y)
            if (d < nd && now - n.at > 60) { nd = d; nearest = n }
        })
        if (!nearest || talking) return
        ctx.save()
        ctx.font = `600 18px ${HAND_FONT}`
        ctx.fillStyle = pal.label
        ctx.globalAlpha = 0.85
        ctx.textAlign = "center"
        const ago = this.#ago(nearest.at)
        const lines = this.#wrap(ctx, `${nearest.name}: “${nearest.text}”`, 260).slice(0, 3)
        lines.forEach((l, i) => ctx.fillText(l, nearest.x, nearest.y - 18 - (lines.length - 1 - i) * 20))
        ctx.font = "500 11px system-ui, sans-serif"
        ctx.globalAlpha = 0.6
        ctx.fillText(ago, nearest.x, nearest.y + 22)
        ctx.restore()
    }

    #drawHearing(ctx, pal) {
        ctx.save()
        ctx.strokeStyle = "#E0A43A"
        ctx.lineWidth = 1.5
        ctx.setLineDash([3, 8])
        ctx.globalAlpha = 0.4
        ctx.beginPath(); ctx.arc(this.me.x, this.me.y, this.full, 0, Math.PI * 2); ctx.stroke()
        ctx.globalAlpha = 0.14
        ctx.beginPath(); ctx.arc(this.me.x, this.me.y, this.zero, 0, Math.PI * 2); ctx.stroke()
        ctx.setLineDash([])
        if (this.me.tx !== null) {
            ctx.globalAlpha = 0.9
            ctx.lineWidth = 2
            ctx.beginPath(); ctx.arc(this.me.tx, this.me.ty, 7, 0, Math.PI * 2); ctx.stroke()
        }
        ctx.restore()
    }

    #drawPlaceHighlight(ctx, pal) {
        const place = this.config.landmarks.find((l) => l.key === this.currentPlace)
        if (!place) return
        ctx.save()
        ctx.strokeStyle = "#E0A43A"
        ctx.lineWidth = 2
        ctx.setLineDash([6, 9])
        ctx.globalAlpha = 0.8
        ctx.beginPath(); ctx.ellipse(place.x, place.y + 6, place.r * 0.85, place.r * 0.65, 0, 0, Math.PI * 2); ctx.stroke()
        ctx.restore()
    }

    #drawFlame(ctx, pal, f) {
        const fl = this.reduceMotion ? 0 : Math.sin(this.clock * 9) * 2 + Math.sin(this.clock * 13.7) * 1.5
        const y = f.y + 2
        ctx.fillStyle = "#E8632F"
        ctx.beginPath(); ctx.moveTo(f.x - 15, y); ctx.quadraticCurveTo(f.x - 12, y - 26 - fl, f.x - 2, y - 42 - fl); ctx.quadraticCurveTo(f.x + 1, y - 26, f.x + 6, y - 34 + fl); ctx.quadraticCurveTo(f.x + 13, y - 18, f.x + 15, y); ctx.closePath(); ctx.fill()
        ctx.fillStyle = "#F6A93B"
        ctx.beginPath(); ctx.moveTo(f.x - 10, y); ctx.quadraticCurveTo(f.x - 7, y - 20 - fl, f.x, y - 30 - fl); ctx.quadraticCurveTo(f.x + 7, y - 18 + fl, f.x + 10, y); ctx.closePath(); ctx.fill()
        ctx.fillStyle = "#FFE08A"
        ctx.beginPath(); ctx.moveTo(f.x - 5, y); ctx.quadraticCurveTo(f.x, y - 16 - fl, f.x + 5, y); ctx.closePath(); ctx.fill()
    }

    // Warm light: the campfire always glows; after dark every lamp, window
    // and screen does too, added on top of the scene.
    // One soft glow, drawn once and stamped wherever there's a light.
    #glowSprite() {
        if (this.glowCanvas) return this.glowCanvas
        const c = document.createElement("canvas")
        c.width = c.height = 128
        const g = c.getContext("2d")
        const grad = g.createRadialGradient(64, 64, 1, 64, 64, 64)
        grad.addColorStop(0, "rgba(255,176,86,1)")
        grad.addColorStop(0.5, "rgba(255,160,70,0.35)")
        grad.addColorStop(1, "rgba(255,160,70,0)")
        g.fillStyle = grad
        g.fillRect(0, 0, 128, 128)
        this.glowCanvas = c
        return c
    }

    #drawGlows(ctx, pal) {
        const fire = this.world.byKey.campfire
        const flicker = this.reduceMotion ? 0 : Math.sin(this.clock * 7) * 6
        const sprite = this.#glowSprite()
        const pad = 200
        const inView = (x, y) => x > this.cam.x - pad && x < this.cam.x + this.viewW + pad && y > this.cam.y - pad && y < this.cam.y + this.viewH + pad
        ctx.save()
        ctx.globalCompositeOperation = "lighter"
        const glow = (x, y, r, a) => { ctx.globalAlpha = Math.min(1, a); ctx.drawImage(sprite, x - r, y - r, r * 2, r * 2) }
        const lamps = this.light ? this.light.lamps : (pal.night ? 1 : 0)
        if (fire && inView(fire.x, fire.y)) glow(fire.x, fire.y - 10, 90 + 100 * lamps + flicker, 0.16 + 0.26 * lamps)
        if (lamps > 0) this.world.lights.forEach((l) => { if (!l.warm && inView(l.x, l.y)) glow(l.x, l.y, l.r, 0.3 * lamps) })
        ctx.restore()
    }

    // Landmark name tags, pre-drawn once per theme and culled to the view.
    #drawLabels(ctx, pal, viewW, viewH) {
        if (this.labelPalette !== pal) { this.labelCache = new Map(); this.labelPalette = pal }
        const k = this.spriteK || 1
        this.world.landmarks.forEach((l) => {
            const x = l.x, y = l.y + l.r * 0.56
            if (x < this.cam.x - 200 || x > this.cam.x + viewW + 200 || y < this.cam.y - 80 || y > this.cam.y + viewH + 80) return
            let tag = this.labelCache.get(l.key)
            if (!tag) {
                const c = document.createElement("canvas")
                const w = 320, h = 60
                c.width = Math.ceil(w * k); c.height = Math.ceil(h * k)
                const g = c.getContext("2d")
                g.setTransform(k, 0, 0, k, w / 2 * k - l.x * k, -(l.y + l.r * 0.56 - 6) * k)
                paintLabel(g, pal, l)
                tag = { c, w, h }
                this.labelCache.set(l.key, tag)
            }
            ctx.drawImage(tag.c, x - tag.w / 2, y - 6, tag.w, tag.h)
        })
    }

    // --- helpers -------------------------------------------------------------------

    #image(src) {
        let img = this.images.get(src)
        if (!img) {
            img = new Image()
            img.referrerPolicy = "no-referrer"
            img.decoding = "async"
            img.src = src
            this.images.set(src, img)
        }
        return img
    }

    #toneFor(id) {
        const tones = ["#C0624A", "#4E7FB0", "#8A6FB8", "#3F8F6B", "#B8873A", "#B0567F"]
        let h = 0
        for (const c of String(id)) h = (h * 31 + c.charCodeAt(0)) >>> 0
        return tones[h % tones.length]
    }

    #wrap(ctx, text, max) {
        const words = String(text).split(/\s+/), lines = []
        let line = ""
        words.forEach((w) => {
            const t = line ? `${line} ${w}` : w
            if (ctx.measureText(t).width > max && line) { lines.push(line); line = w } else line = t
        })
        if (line) lines.push(line)
        return lines
    }

    #roundRect(ctx, x, y, w, h, r) {
        ctx.beginPath()
        ctx.moveTo(x + r, y)
        ctx.arcTo(x + w, y, x + w, y + h, r)
        ctx.arcTo(x + w, y + h, x, y + h, r)
        ctx.arcTo(x, y + h, x, y, r)
        ctx.arcTo(x, y, x + w, y, r)
        ctx.closePath()
    }

    #ago(at) {
        const s = Math.max(0, this.#now() - at)
        if (s < 90) return "just now"
        if (s < 3600) return `${Math.round(s / 60)} min ago`
        const h = Math.round(s / 3600)
        return h === 1 ? "an hour ago" : `${h} hours ago`
    }
}
